import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';

const LightingCanvas = forwardRef((
  { 
    image, 
    lights, 
    ambientDarkness, 
    selectedLightId, 
    onLightDrag,
    onGrazerDrag,
    onLightSelect,
    previewMode,
    logos = [], // Multiple logos array
    selectedLogoId,
    onLogoSelect,
    onLogoPositionChange,
    isDrawingNeon,
    onNeonDrawingComplete,
    selectedNeonPointIndex,
    onNeonPointSelect,
    onNeonPathUpdate,
    zoomLevel = 1,
    panOffset = { x: 0, y: 0 },
    onPanChange,
    isPanning = false,
    setIsPanning,
    panStartRef,
    lastPanOffsetRef,
    occluders = [],
    selectedOccluderId = null,
    onOccluderSelect,
    onOccluderPositionChange,
    onOccluderResize,
    onNeonResize,
    isDrawingOccluder = false,
    onOccluderDrawingComplete,
    fitMode = 'fit',
    customWidth = 1920,
    customHeight = 1080,
  },
  ref
) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const offscreenCanvasRef = useRef(null); // Offscreen canvas for light masking
  const logoImagesRef = useRef({}); // Cache for loaded logo images
  const [isDragging, setIsDragging] = useState(false);
  const [draggedLightId, setDraggedLightId] = useState(null);
  const [draggedLogoId, setDraggedLogoId] = useState(null); // Track which logo is being dragged;
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [draggedOccluderId, setDraggedOccluderId] = useState(null);  const [isDraggingOccluder, setIsDraggingOccluder] = useState(false);
  const [draggedOccluderHandle, setDraggedOccluderHandle] = useState(null); // 'tl'|'tr'|'br'|'bl'
  // Neon resize state — 'tl'|'tr'|'br'|'bl' (corners) | 't'|'r'|'b'|'l' (edges)
  const [draggedNeonId, setDraggedNeonId] = useState(null);
  const [draggedNeonHandle, setDraggedNeonHandle] = useState(null);
  const draggedNeonStartRef = useRef(null); // { path: [...], bbox: {...}, shiftKey: bool }
  const draggedNeonMoveRef = useRef(null); // { path: [...], startX: %, startY: % } — bbox-interior translate
  const [occluderDrawPath, setOccluderDrawPath] = useState([]); // freehand points [{x%, y%}]
  const occluderMoveLastRef = useRef(null); // last drag position for polygon translation
  const [neonDrawPoints, setNeonDrawPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draggedNeonPoint, setDraggedNeonPoint] = useState(null);
  const [draggedGrazerPart, setDraggedGrazerPart] = useState(null); // 'start', 'end', or 'line'
  const [spacePressed, setSpacePressed] = useState(false);
  
  // Touch gesture state for pinch-to-zoom and two-finger pan
  const [touchStartDist, setTouchStartDist] = useState(null);
  const [touchStartZoom, setTouchStartZoom] = useState(1);
  const [touchPanStart, setTouchPanStart] = useState(null);
  const [isTouchPanning, setIsTouchPanning] = useState(false);
  
  // Neon drawing enhancements
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const magnifierCanvasRef = useRef(null);

  // Helper to get distance between two touch points
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Helper to get center point between two touches
  const getTouchCenter = (touches) => {
    if (touches.length < 2) return null;
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  // Handle spacebar for panning
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat && zoomLevel > 1) {
        e.preventDefault();
        setSpacePressed(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        setSpacePressed(false);
        if (setIsPanning) setIsPanning(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [zoomLevel, setIsPanning]);

  useImperativeHandle(ref, () => ({
    getCanvas: () => canvasRef.current,
  }));

  useEffect(() => {
    const img = new Image();
    img.src = image;
    img.onload = () => {
      imageRef.current = img;
      drawCanvas();
    };
  }, [image]);
  
  // Load logo images for multiple logos
  useEffect(() => {
    logos.forEach(logo => {
      if (logo && logo.image && !logoImagesRef.current[logo.id]) {
        const logoImg = new Image();
        logoImg.src = logo.image;
        logoImg.onload = () => {
          logoImagesRef.current[logo.id] = logoImg;
          drawCanvas();
        };
      }
    });
    // Clean up removed logos
    Object.keys(logoImagesRef.current).forEach(id => {
      if (!logos.find(l => l.id === parseInt(id))) {
        delete logoImagesRef.current[id];
      }
    });
  }, [logos]);

  useEffect(() => {
    if (imageRef.current) {
      drawCanvas();
    }
  }, [lights, ambientDarkness, selectedLightId, neonDrawPoints, previewMode, logos, occluders, selectedOccluderId, isDrawingOccluder, occluderDrawPath, fitMode]);

  // Update magnifier canvas to show zoomed area
  useEffect(() => {
    if (!showMagnifier || !magnifierCanvasRef.current || !canvasRef.current) return;
    
    const magnifierCanvas = magnifierCanvasRef.current;
    const mainCanvas = canvasRef.current;
    const magnifierCtx = magnifierCanvas.getContext('2d');
    const rect = mainCanvas.getBoundingClientRect();
    
    // Calculate position on main canvas
    const canvasX = ((magnifierPos.x - rect.left) / rect.width) * mainCanvas.width;
    const canvasY = ((magnifierPos.y - rect.top) / rect.height) * mainCanvas.height;
    
    // Clear magnifier
    magnifierCtx.clearRect(0, 0, magnifierCanvas.width, magnifierCanvas.height);
    
    // Draw zoomed portion (3x zoom)
    const zoomFactor = 3;
    const sourceSize = magnifierCanvas.width / zoomFactor;
    
    magnifierCtx.drawImage(
      mainCanvas,
      canvasX - sourceSize / 2, // source x
      canvasY - sourceSize / 2, // source y
      sourceSize, // source width
      sourceSize, // source height
      0, // dest x
      0, // dest y
      magnifierCanvas.width, // dest width
      magnifierCanvas.height // dest height
    );
    
    // Draw crosshair
    magnifierCtx.strokeStyle = 'rgba(34, 211, 238, 0.8)';
    magnifierCtx.lineWidth = 1.5;
    magnifierCtx.shadowBlur = 3;
    magnifierCtx.shadowColor = 'rgba(34, 211, 238, 0.6)';
    magnifierCtx.beginPath();
    // Vertical line
    magnifierCtx.moveTo(magnifierCanvas.width / 2, 0);
    magnifierCtx.lineTo(magnifierCanvas.width / 2, magnifierCanvas.height);
    // Horizontal line
    magnifierCtx.moveTo(0, magnifierCanvas.height / 2);
    magnifierCtx.lineTo(magnifierCanvas.width, magnifierCanvas.height / 2);
    magnifierCtx.stroke();
    
    // Draw center dot
    magnifierCtx.beginPath();
    magnifierCtx.arc(magnifierCanvas.width / 2, magnifierCanvas.height / 2, 3, 0, 2 * Math.PI);
    magnifierCtx.fillStyle = 'rgba(34, 211, 238, 0.9)';
    magnifierCtx.fill();
    
  }, [showMagnifier, magnifierPos, lights, neonDrawPoints]);

  // Color temperature to RGB mapping
  const getColorFromTemp = (temp) => {
    // Realistic color temperature curve from 2200K to 6500K
    // Based on actual blackbody radiation colors
    
    if (temp <= 2200) {
      return { r: 255, g: 197, b: 143 }; // Very warm candlelight
    } else if (temp <= 2700) {
      // 2200K to 2700K - Warm incandescent
      const t = (temp - 2200) / 500;
      return {
        r: 255,
        g: Math.round(197 + (245 - 197) * t),
        b: Math.round(143 + (220 - 143) * t)
      };
    } else if (temp <= 3000) {
      // 2700K to 3000K - Standard warm white
      const t = (temp - 2700) / 300;
      return {
        r: 255,
        g: Math.round(245 + (250 - 245) * t),
        b: Math.round(220 + (240 - 220) * t)
      };
    } else if (temp <= 4000) {
      // 3000K to 4000K - Neutral white
      const t = (temp - 3000) / 1000;
      return {
        r: 255,
        g: Math.round(250 + (255 - 250) * t),
        b: Math.round(240 + (250 - 240) * t)
      };
    } else if (temp <= 5000) {
      // 4000K to 5000K - Cool white
      const t = (temp - 4000) / 1000;
      return {
        r: Math.round(255 - (255 - 245) * t),
        g: 255,
        b: Math.round(250 + (255 - 250) * t)
      };
    } else if (temp <= 6500) {
      // 5000K to 6500K - Daylight to cool daylight
      const t = (temp - 5000) / 1500;
      return {
        r: Math.round(245 - (245 - 225) * t),
        g: Math.round(255 - (255 - 238) * t),
        b: 255
      };
    } else {
      // 6500K+ - Cool daylight
      return { r: 225, g: 238, b: 255 };
    }
  };

  // Convert hex color to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 245, b: 220 };
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !imageRef.current || !container) return;

    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // ── Compute canvas dimensions from fitMode ─────────────────────────
    // We anchor the available height to the VIEWPORT (not the container)
    // because the container's height grows to fit the canvas, creating a
    // feedback loop that would otherwise make all 3 modes converge.
    const containerWidth = container.offsetWidth;
    // Reserve ~60 px for the canvas top toolbar + 80 px for bottom controls
    const availableHeight = Math.max(300, window.innerHeight - 140);
    const aspect = img.height / img.width; // h / w

    let canvasWidth;
    let canvasHeight;
    if (fitMode === 'custom') {
      // Use user-specified custom dimensions
      canvasWidth = customWidth;
      canvasHeight = customHeight;
    } else if (fitMode === 'height') {
      // Fill viewport height; let width follow aspect (clamp if it overflows)
      canvasHeight = availableHeight;
      canvasWidth = canvasHeight / aspect;
      if (canvasWidth > containerWidth) {
        canvasWidth = containerWidth;
        canvasHeight = canvasWidth * aspect;
      }
    } else if (fitMode === 'fit') {
      // Contain the WHOLE image — pick the smaller of width-fit / height-fit
      // so nothing is cropped and the image doesn't exceed the viewport.
      const scaleByWidth = containerWidth / img.width;
      const scaleByHeight = availableHeight / img.height;
      const scale = Math.min(scaleByWidth, scaleByHeight);
      canvasWidth = img.width * scale;
      canvasHeight = img.height * scale;
    } else {
      // 'width' — legacy: canvas matches container width, height follows aspect
      canvasWidth = containerWidth;
      canvasHeight = canvasWidth * aspect;
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    // Force the displayed CSS size to match the intrinsic bitmap size so parent
    // flex/100% styles can't stretch the canvas and defeat the fit-mode math.
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    // Draw the base image
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Apply realistic ambient darkness with color temperature shift
    // 0 = daylight (no overlay), 100 = deep night (heavy overlay)
    if (ambientDarkness > 0) {
      // Create realistic evening/night effect with color temperature shift
      const darknessLevel = ambientDarkness / 100;
      
      // Evening to night color progression
      // Evening: Warm orangey blue (golden hour)
      // Twilight: Deep blue with purple hints
      // Night: Deep blue-black with slight purple
      
      let overlayColor;
      let overlayAlpha;
      
      if (darknessLevel < 0.3) {
        // Early evening - subtle warm darkening (golden hour)
        const t = darknessLevel / 0.3;
        overlayColor = {
          r: 20 + (40 * (1 - t)),  // Warm orange tint
          g: 25 + (35 * (1 - t)),
          b: 45 + (20 * (1 - t))
        };
        overlayAlpha = 0.2 + (t * 0.15); // 20-35% darkness
        
      } else if (darknessLevel < 0.6) {
        // Twilight - deep blue evening
        const t = (darknessLevel - 0.3) / 0.3;
        overlayColor = {
          r: 15 + (5 * (1 - t)),   // Less warmth
          g: 20 + (10 * (1 - t)),
          b: 50 + (15 * t)         // More blue
        };
        overlayAlpha = 0.35 + (t * 0.25); // 35-60% darkness
        
      } else {
        // Deep night - very dark with blue-black tones
        const t = (darknessLevel - 0.6) / 0.4;
        overlayColor = {
          r: 10 + (5 * (1 - t)),
          g: 15 + (5 * (1 - t)),
          b: 35 + (30 * t)
        };
        overlayAlpha = 0.6 + (t * 0.25); // 60-85% darkness (preserve some detail)
      }
      
      // Apply the realistic overlay
      ctx.fillStyle = `rgba(${overlayColor.r}, ${overlayColor.g}, ${overlayColor.b}, ${overlayAlpha})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add subtle atmospheric gradient for night sky effect (darker at top)
      if (darknessLevel > 0.4) {
        const skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        const skyAlpha = (darknessLevel - 0.4) * 0.15; // Subtle gradient
        skyGradient.addColorStop(0, `rgba(10, 15, 40, ${skyAlpha})`);
        skyGradient.addColorStop(0.4, `rgba(10, 15, 40, ${skyAlpha * 0.5})`);
        skyGradient.addColorStop(1, `rgba(10, 15, 40, 0)`);
        ctx.fillStyle = skyGradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    // Draw each light — route through offscreen canvas if occluders are active
    const activeOccluders = (occluders || []).filter(o => !o.hidden);
    if (activeOccluders.length > 0) {
      // Offscreen render path: draw BASE IMAGE + lights to offscreen, mask, composite
      if (!offscreenCanvasRef.current) {
        offscreenCanvasRef.current = document.createElement('canvas');
      }
      const off = offscreenCanvasRef.current;
      off.width = canvas.width;
      off.height = canvas.height;
      const offCtx = off.getContext('2d');
      
      // CRITICAL FIX: Draw the base image first so lights blend correctly
      if (imageRef.current) {
        offCtx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
      }
      
      // Apply ambient darkness
      if (ambientDarkness > 0) {
        const darkAmount = ambientDarkness / 100;
        offCtx.fillStyle = `rgba(0, 0, 20, ${darkAmount * 0.85})`;
        offCtx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Now draw lights on top of the image (they'll blend naturally)
      lights.forEach((light) => {
        drawLight(offCtx, off, light);
      });

      // Apply occluder masks using destination-out with soft shadow blur for soft edges
      offCtx.save();
      offCtx.globalCompositeOperation = 'destination-out';
      activeOccluders.forEach((occ) => {
        const softness = Math.max(0, occ.softness ?? 10);
        const opacity = Math.max(0, Math.min(1, occ.opacity ?? 1));
        offCtx.save();
        if (softness > 0) {
          offCtx.shadowColor = 'rgba(0,0,0,1)';
          offCtx.shadowBlur = softness;
        }
        offCtx.fillStyle = `rgba(0,0,0,${opacity})`;

        if (occ.type === 'polygon' && Array.isArray(occ.points) && occ.points.length >= 3) {
          // Polygon mask
          offCtx.beginPath();
          occ.points.forEach((p, i) => {
            const px = (p.x / 100) * canvas.width;
            const py = (p.y / 100) * canvas.height;
            if (i === 0) offCtx.moveTo(px, py);
            else offCtx.lineTo(px, py);
          });
          offCtx.closePath();
          offCtx.fill();
        } else {
          // Rectangle mask (legacy / Add button)
          const cx = (occ.x / 100) * canvas.width;
          const cy = (occ.y / 100) * canvas.height;
          const w = Math.max(1, (occ.width / 100) * canvas.width);
          const h = Math.max(1, (occ.height / 100) * canvas.height);
          offCtx.translate(cx, cy);
          offCtx.rotate(((occ.rotation || 0) * Math.PI) / 180);
          offCtx.fillRect(-w / 2, -h / 2, w, h);
        }
        offCtx.restore();
      });
      offCtx.restore();

      // Composite the offscreen canvas back - use default source-over but ensure proper blending
      ctx.drawImage(off, 0, 0);
    } else {
      lights.forEach((light) => {
        drawLight(ctx, canvas, light);
      });
    }

    // Draw occluder outlines (only in edit mode, not preview/presentation)
    if (!previewMode && (occluders || []).length > 0) {
      occluders.forEach((occ) => {
        const isSelected = occ.id === selectedOccluderId;
        const dim = occ.hidden ? 0.35 : 1;
        const strokeStyle = isSelected
          ? `rgba(34, 211, 238, ${0.95 * dim})`
          : `rgba(251, 191, 36, ${0.75 * dim})`;
        const fillDot = isSelected ? 'rgba(34, 211, 238, 0.9)' : 'rgba(251, 191, 36, 0.7)';

        if (occ.type === 'polygon' && Array.isArray(occ.points) && occ.points.length >= 3) {
          // Polygon outline
          ctx.save();
          ctx.strokeStyle = strokeStyle;
          ctx.fillStyle = isSelected ? `rgba(34, 211, 238, ${0.08 * dim})` : `rgba(251, 191, 36, ${0.06 * dim})`;
          ctx.lineWidth = isSelected ? 2.5 : 1.5;
          ctx.setLineDash([8, 6]);
          ctx.beginPath();
          occ.points.forEach((p, i) => {
            const px = (p.x / 100) * canvas.width;
            const py = (p.y / 100) * canvas.height;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          });
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.setLineDash([]);
          // Centroid dot
          let cxSum = 0, cySum = 0;
          occ.points.forEach(p => { cxSum += p.x; cySum += p.y; });
          const cxPct = cxSum / occ.points.length;
          const cyPct = cySum / occ.points.length;
          const cxPx = (cxPct / 100) * canvas.width;
          const cyPx = (cyPct / 100) * canvas.height;
          ctx.fillStyle = fillDot;
          ctx.beginPath();
          ctx.arc(cxPx, cyPx, 3, 0, 2 * Math.PI);
          ctx.fill();
          if (occ.hidden) {
            ctx.fillStyle = 'rgba(148, 163, 184, 0.8)';
            ctx.font = '11px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('hidden', cxPx, cyPx + 16);
          }
          ctx.restore();
        } else {
          // Rectangle outline (legacy)
          const cx = (occ.x / 100) * canvas.width;
          const cy = (occ.y / 100) * canvas.height;
          const w = (occ.width / 100) * canvas.width;
          const h = (occ.height / 100) * canvas.height;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(((occ.rotation || 0) * Math.PI) / 180);
          ctx.strokeStyle = strokeStyle;
          ctx.lineWidth = isSelected ? 2.5 : 1.5;
          ctx.setLineDash([8, 6]);
          ctx.strokeRect(-w / 2, -h / 2, w, h);
          ctx.setLineDash([]);
          ctx.fillStyle = fillDot;
          ctx.beginPath();
          ctx.arc(0, 0, 3, 0, 2 * Math.PI);
          ctx.fill();
          if (occ.hidden) {
            ctx.fillStyle = 'rgba(148, 163, 184, 0.8)';
            ctx.font = '11px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('hidden', 0, h / 2 + 14);
          }
          // Corner handles only for rectangles
          if (isSelected && !occ.hidden) {
            const handleR = 7;
            const handlePositions = [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]];
            handlePositions.forEach(([hx, hy]) => {
              ctx.beginPath();
              ctx.arc(hx, hy, handleR, 0, 2 * Math.PI);
              ctx.fillStyle = 'rgba(34, 211, 238, 0.95)';
              ctx.fill();
              ctx.strokeStyle = 'rgba(8, 47, 73, 0.9)';
              ctx.lineWidth = 1.5;
              ctx.stroke();
            });
          }
          ctx.restore();
        }
      });
    }

    // Draw occluder drawing preview (freehand polyline in progress)
    if (!previewMode && isDrawingOccluder && occluderDrawPath.length > 0) {
      ctx.save();
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.95)';
      ctx.fillStyle = 'rgba(251, 191, 36, 0.15)';
      ctx.lineWidth = 2.5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      occluderDrawPath.forEach((p, i) => {
        const px = (p.x / 100) * canvas.width;
        const py = (p.y / 100) * canvas.height;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      // If we have enough points, show the close hint as a faint line back to start
      if (occluderDrawPath.length >= 3) {
        ctx.fill();
      }
      ctx.stroke();
      ctx.setLineDash([]);
      // Dots at first/last point
      if (occluderDrawPath.length > 0) {
        const first = occluderDrawPath[0];
        const fpx = (first.x / 100) * canvas.width;
        const fpy = (first.y / 100) * canvas.height;
        ctx.fillStyle = 'rgba(251, 191, 36, 1)';
        ctx.beginPath();
        ctx.arc(fpx, fpy, 5, 0, 2 * Math.PI);
        ctx.fill();
      }
      ctx.restore();
    }
    
    // Draw neon drawing preview (while user is drawing)
    if (isDrawing && neonDrawPoints.length > 1) {
      const canvasPath = neonDrawPoints.map(point => ({
        x: (point.x / 100) * canvas.width,
        y: (point.y / 100) * canvas.height
      }));
      
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 100, 255, 0.8)';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowBlur = 15;
      ctx.shadowColor = 'rgba(255, 100, 255, 0.6)';
      
      drawSmoothPath(ctx, canvasPath);
      
      ctx.restore();
    }

    // Highlight selected light (only if not in preview mode)
    if (selectedLightId && !previewMode) {
      const selectedLight = lights.find(l => l.id === selectedLightId);
      if (selectedLight && selectedLight.beamType !== 'neon' && selectedLight.beamType !== 'grazer') {
        // Only draw selection ring for beam lights (neon and grazer don't have single position)
        const lightX = (selectedLight.position.x / 100) * canvas.width;
        const lightY = (selectedLight.position.y / 100) * canvas.height;
        
        // Draw selection ring
        ctx.beginPath();
        ctx.arc(lightX, lightY, 14, 0, 2 * Math.PI);
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      
      // Draw selection for neon lights — bbox with 8 resize handles
      if (selectedLight && selectedLight.beamType === 'neon') {
        const bbox = getNeonBBox(selectedLight);
        if (bbox) {
          const x0 = (bbox.minX / 100) * canvas.width;
          const y0 = (bbox.minY / 100) * canvas.height;
          const x1 = (bbox.maxX / 100) * canvas.width;
          const y1 = (bbox.maxY / 100) * canvas.height;
          ctx.strokeStyle = 'rgba(34, 211, 238, 0.7)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 4]);
          ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
          ctx.setLineDash([]);
          for (const h of getNeonHandlePositions(bbox)) {
            const hx = (h.x / 100) * canvas.width;
            const hy = (h.y / 100) * canvas.height;
            ctx.beginPath();
            ctx.rect(hx - 5, hy - 5, 10, 10);
            ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
            ctx.fill();
            ctx.strokeStyle = 'rgba(34, 211, 238, 0.95)';
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        }
      }

      // Draw selection for grazer lights (highlight the line with handles)
      if (selectedLight && selectedLight.beamType === 'grazer' && selectedLight.startPoint && selectedLight.endPoint) {        const startX = (selectedLight.startPoint.x / 100) * canvas.width;
        const startY = (selectedLight.startPoint.y / 100) * canvas.height;
        const endX = (selectedLight.endPoint.x / 100) * canvas.width;
        const endY = (selectedLight.endPoint.y / 100) * canvas.height;
        
        // Draw selection line
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Draw end handles
        const handleRadius = 8;
        ctx.beginPath();
        ctx.arc(startX, startY, handleRadius, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        ctx.beginPath();
        ctx.arc(endX, endY, handleRadius, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
    
    // Draw all logos/watermarks
    logos.forEach(logo => {
      const logoImg = logoImagesRef.current[logo.id];
      if (!logoImg || !logo.image) return;
      
      ctx.save();
      ctx.globalAlpha = logo.opacity / 100;
      
      const logoWidth = (logo.size / 100) * canvas.width;
      const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
      
      if (logo.tiled) {
        // Tiled pattern across entire canvas
        const cols = Math.ceil(canvas.width / logoWidth) + 1;
        const rows = Math.ceil(canvas.height / logoHeight) + 1;
        
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const x = col * logoWidth * 1.2;
            const y = row * logoHeight * 1.2;
            
            ctx.save();
            ctx.translate(x + logoWidth / 2, y + logoHeight / 2);
            ctx.rotate((logo.rotation * Math.PI) / 180);
            ctx.drawImage(
              logoImg,
              -logoWidth / 2,
              -logoHeight / 2,
              logoWidth,
              logoHeight
            );
            ctx.restore();
          }
        }
      } else {
        // Single logo at custom position
        const logoX = (logo.position.x / 100) * canvas.width;
        const logoY = (logo.position.y / 100) * canvas.height;
        
        ctx.translate(logoX, logoY);
        ctx.rotate((logo.rotation * Math.PI) / 180);
        ctx.drawImage(
          logoImg,
          -logoWidth / 2,
          -logoHeight / 2,
          logoWidth,
          logoHeight
        );
        
        // Draw selection border if this logo is selected (not in preview mode)
        if (logo.id === selectedLogoId && !previewMode) {
          ctx.strokeStyle = 'rgba(34, 211, 238, 0.8)';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(-logoWidth / 2 - 5, -logoHeight / 2 - 5, logoWidth + 10, logoHeight + 10);
          ctx.setLineDash([]);
        }
      }
      
      ctx.restore();
    });
  };

  // Draw neon light with smooth curves and glow effect
  const drawNeonLight = (ctx, canvas, light) => {
    if (!light.path || light.path.length < 2) return;
    
    const thickness = light.thickness || 3;
    const glowRadius = light.glowRadius || 20;
    const brightness = (light.brightness || 50) / 100;
    
    // Determine color
    let color;
    if (light.customColor) {
      color = hexToRgb(light.customColor);
    } else {
      const colorTemp = light.colorTemp || 2300;
      color = getColorFromTemp(colorTemp);
    }
    
    // Convert path points from percentage to canvas coordinates
    const canvasPath = light.path.map(point => ({
      x: (point.x / 100) * canvas.width,
      y: (point.y / 100) * canvas.height
    }));
    
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    
    // Draw outer glow layers for realistic neon effect
    for (let layer = 3; layer >= 1; layer--) {
      const layerRadius = glowRadius * (layer / 3);
      const layerOpacity = brightness * (0.15 / layer);
      
      ctx.shadowBlur = layerRadius;
      ctx.shadowColor = `rgba(${color.r}, ${color.g}, ${color.b}, ${layerOpacity})`;
      ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${layerOpacity})`;
      ctx.lineWidth = thickness + (layerRadius * 0.5);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      drawSmoothPath(ctx, canvasPath);
    }
    
    // Draw core neon tube (brightest)
    ctx.shadowBlur = thickness * 2;
    ctx.shadowColor = `rgba(${color.r}, ${color.g}, ${color.b}, ${brightness})`;
    ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${brightness * 0.9})`;
    ctx.lineWidth = thickness;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    drawSmoothPath(ctx, canvasPath);
    
    // Draw inner highlight for glass tube effect
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `rgba(255, 255, 255, ${brightness * 0.6})`;
    ctx.lineWidth = thickness * 0.3;
    
    drawSmoothPath(ctx, canvasPath);
    
    ctx.restore();
  };
  
  // Draw smooth curved path through points using quadratic curves.
  // Detects sharp corners (turn angle > ~60°) and breaks the smoothing there
  // so hand-drawn squares/triangles keep crisp corners instead of being
  // rounded into blobs. Gentle curves remain smooth.
  const drawSmoothPath = (ctx, points) => {
    if (points.length < 2) return;

    if (points.length === 2) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      ctx.lineTo(points[1].x, points[1].y);
      ctx.stroke();
      return;
    }

    // Threshold: how much the path must turn at a vertex to be treated as a corner.
    // 60° = sharp angle (square/triangle corners turn 90°/120°); gentle curves are <30°.
    const SHARP_CORNER_RAD = (60 * Math.PI) / 180;

    // Mark interior points that are sharp corners.
    const isSharp = new Array(points.length).fill(false);
    for (let i = 1; i < points.length - 1; i++) {
      const v1x = points[i].x - points[i - 1].x;
      const v1y = points[i].y - points[i - 1].y;
      const v2x = points[i + 1].x - points[i].x;
      const v2y = points[i + 1].y - points[i].y;
      const len1 = Math.hypot(v1x, v1y);
      const len2 = Math.hypot(v2x, v2y);
      if (len1 < 0.01 || len2 < 0.01) continue;
      const cos = (v1x * v2x + v1y * v2y) / (len1 * len2);
      const turn = Math.acos(Math.max(-1, Math.min(1, cos))); // 0 = straight, π = U-turn
      if (turn > SHARP_CORNER_RAD) isSharp[i] = true;
    }

    // Split the path into smooth segments at each sharp corner.
    // Corner points appear as the END of one segment and the START of the next,
    // so the join is exact and no curve crosses a corner.
    const segments = [];
    let current = [points[0]];
    for (let i = 1; i < points.length; i++) {
      current.push(points[i]);
      if (isSharp[i] && i < points.length - 1) {
        segments.push(current);
        current = [points[i]];
      }
    }
    segments.push(current);

    // Render: each segment uses quadratic-curve smoothing, joined by straight
    // segments where corners were detected.
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (const seg of segments) {
      if (seg.length === 2) {
        ctx.lineTo(seg[1].x, seg[1].y);
        continue;
      }
      // Ensure pen is exactly at the segment start (no-op for first segment;
      // robust join after a corner for subsequent segments).
      ctx.lineTo(seg[0].x, seg[0].y);
      for (let i = 1; i < seg.length - 1; i++) {
        const xc = (seg[i].x + seg[i + 1].x) / 2;
        const yc = (seg[i].y + seg[i + 1].y) / 2;
        ctx.quadraticCurveTo(seg[i].x, seg[i].y, xc, yc);
      }
      const last = seg[seg.length - 1];
      const secondLast = seg[seg.length - 2];
      ctx.quadraticCurveTo(secondLast.x, secondLast.y, last.x, last.y);
    }

    ctx.stroke();
  };

  // Draw grazer linear light with wall wash effect
  const drawGrazerLight = (ctx, canvas, light) => {
    if (!light.startPoint || !light.endPoint) return;
    
    const intensity = (light.intensity || 50) / 100;
    const throwDistance = (light.throwDistance || 1.5) * Math.max(canvas.width, canvas.height) * 0.4;
    const throwDirection = ((light.throwDirection || 270) * Math.PI) / 180;
    const sourceSize = light.sourceSize || 1; // Source size multiplier
    const sourceHidden = light.sourceHidden || false; // Whether to hide the fixture
    
    // Determine color
    let color;
    if (light.customColor) {
      color = hexToRgb(light.customColor);
    } else {
      const colorTemp = light.colorTemp || 2300;
      color = getColorFromTemp(colorTemp);
    }
    
    // Convert points to canvas coordinates
    const startX = (light.startPoint.x / 100) * canvas.width;
    const startY = (light.startPoint.y / 100) * canvas.height;
    const endX = (light.endPoint.x / 100) * canvas.width;
    const endY = (light.endPoint.y / 100) * canvas.height;
    
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    
    // Calculate perpendicular direction for light throw
    const lineAngle = Math.atan2(endY - startY, endX - startX);
    
    // === HAZY EDGE EFFECT for Grazer - draw feathered outer layers first ===
    const hazeSteps = 9;
    const hazeSpreadMax = 0.15; // Max angular spread for haze (in radians)
    
    for (let h = hazeSteps; h >= 1; h--) {
      const hazeRatio = h / hazeSteps;
      const hazeAngleOffset = hazeSpreadMax * hazeRatio;
      const hazeOpacity = 0.27 * (1 - hazeRatio * 0.55) * intensity;
      
      const numSegments = 20;
      for (let i = 0; i < numSegments; i++) {
        const t = i / numSegments;
        const segStartX = startX + (endX - startX) * t;
        const segStartY = startY + (endY - startY) * t;
        const segEndX = startX + (endX - startX) * (t + 1 / numSegments);
        const segEndY = startY + (endY - startY) * (t + 1 / numSegments);
        
        const scaledThrowDistance = throwDistance * sourceSize;
        
        // Left side haze
        const hazeThrowDirLeft = throwDirection - hazeAngleOffset;
        const throwEndX1L = segStartX + Math.cos(hazeThrowDirLeft) * scaledThrowDistance;
        const throwEndY1L = segStartY + Math.sin(hazeThrowDirLeft) * scaledThrowDistance;
        const throwEndX2L = segEndX + Math.cos(hazeThrowDirLeft) * scaledThrowDistance;
        const throwEndY2L = segEndY + Math.sin(hazeThrowDirLeft) * scaledThrowDistance;
        
        // Right side haze
        const hazeThrowDirRight = throwDirection + hazeAngleOffset;
        const throwEndX1R = segStartX + Math.cos(hazeThrowDirRight) * scaledThrowDistance;
        const throwEndY1R = segStartY + Math.sin(hazeThrowDirRight) * scaledThrowDistance;
        const throwEndX2R = segEndX + Math.cos(hazeThrowDirRight) * scaledThrowDistance;
        const throwEndY2R = segEndY + Math.sin(hazeThrowDirRight) * scaledThrowDistance;
        
        const hazeGradient = ctx.createLinearGradient(
          segStartX, segStartY,
          segStartX + Math.cos(throwDirection) * scaledThrowDistance,
          segStartY + Math.sin(throwDirection) * scaledThrowDistance
        );
        hazeGradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${hazeOpacity})`);
        hazeGradient.addColorStop(0.3, `rgba(${color.r}, ${color.g}, ${color.b}, ${hazeOpacity * 0.6})`);
        hazeGradient.addColorStop(0.6, `rgba(${color.r}, ${color.g}, ${color.b}, ${hazeOpacity * 0.3})`);
        hazeGradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
        
        // Draw left haze segment
        ctx.beginPath();
        ctx.moveTo(segStartX, segStartY);
        ctx.lineTo(throwEndX1L, throwEndY1L);
        ctx.lineTo(throwEndX2L, throwEndY2L);
        ctx.lineTo(segEndX, segEndY);
        ctx.closePath();
        ctx.fillStyle = hazeGradient;
        ctx.fill();
        
        // Draw right haze segment
        ctx.beginPath();
        ctx.moveTo(segStartX, segStartY);
        ctx.lineTo(throwEndX1R, throwEndY1R);
        ctx.lineTo(throwEndX2R, throwEndY2R);
        ctx.lineTo(segEndX, segEndY);
        ctx.closePath();
        ctx.fillStyle = hazeGradient;
        ctx.fill();
      }
    }
    // === END HAZY EDGE EFFECT ===
    
    // Create wall wash effect - multiple gradients from the fixture line (main beam)
    const numSegments = 20; // More segments = smoother gradient
    for (let i = 0; i < numSegments; i++) {
      const t = i / numSegments;
      const segStartX = startX + (endX - startX) * t;
      const segStartY = startY + (endY - startY) * t;
      const segEndX = startX + (endX - startX) * (t + 1 / numSegments);
      const segEndY = startY + (endY - startY) * (t + 1 / numSegments);
      
      // Calculate throw endpoints - scale by sourceSize
      const scaledThrowDistance = throwDistance * sourceSize;
      const throwEndX1 = segStartX + Math.cos(throwDirection) * scaledThrowDistance;
      const throwEndY1 = segStartY + Math.sin(throwDirection) * scaledThrowDistance;
      const throwEndX2 = segEndX + Math.cos(throwDirection) * scaledThrowDistance;
      const throwEndY2 = segEndY + Math.sin(throwDirection) * scaledThrowDistance;
      
      // Create gradient for wall wash effect
      const gradient = ctx.createLinearGradient(
        segStartX, segStartY,
        (throwEndX1 + throwEndX2) / 2, (throwEndY1 + throwEndY2) / 2
      );
      
      gradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity * 0.8})`);
      gradient.addColorStop(0.3, `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity * 0.5})`);
      gradient.addColorStop(0.7, `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity * 0.2})`);
      gradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
      
      // Draw wash segment
      ctx.beginPath();
      ctx.moveTo(segStartX, segStartY);
      ctx.lineTo(throwEndX1, throwEndY1);
      ctx.lineTo(throwEndX2, throwEndY2);
      ctx.lineTo(segEndX, segEndY);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    }
    
    // Draw fixture line (the actual LED bar) - only if not hidden
    if (!sourceHidden) {
      const fixtureLineWidth = 4 * sourceSize; // Scale line width by sourceSize
      const coreLineWidth = 2 * sourceSize;
      
      ctx.shadowBlur = 15 * sourceSize;
      ctx.shadowColor = `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity})`;
      ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity * 0.9})`;
      ctx.lineWidth = fixtureLineWidth;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      
      // Draw bright core on fixture
      ctx.shadowBlur = 0;
      ctx.strokeStyle = `rgba(255, 255, 255, ${intensity * 0.7})`;
      ctx.lineWidth = coreLineWidth;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }

    // Bounce spill glow — ambient halo around wash when Realistic Wall Throw is on
    if (light.surfaceThrow) {
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      const scaledThrow = throwDistance * sourceSize;
      const spillCX = midX + Math.cos(throwDirection) * (scaledThrow * 0.45);
      const spillCY = midY + Math.sin(throwDirection) * (scaledThrow * 0.45);
      const spillR = scaledThrow * 1.4;
      const spillGrad = ctx.createRadialGradient(spillCX, spillCY, 0, spillCX, spillCY, spillR);
      spillGrad.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.16 * intensity})`);
      spillGrad.addColorStop(0.4, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.07 * intensity})`);
      spillGrad.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
      ctx.fillStyle = spillGrad;
      ctx.beginPath();
      ctx.arc(spillCX, spillCY, spillR, 0, 2 * Math.PI);
      ctx.fill();
    }

    ctx.restore();
  };

  // === Realistic Wall-Throw rendering for beam lights ===
  // Simulates light hitting a nearby surface: hot concentrated base,
  // triangular fan wash, fast falloff at far end, ambient bounce spill.
  const drawSurfaceThrowBeam = (ctx, canvas, light) => {
    const lightX = (light.position.x / 100) * canvas.width;
    const lightY = (light.position.y / 100) * canvas.height;
    const intensity = (light.intensity || 50) / 100;
    const sourceSize = light.sourceSize || 1;
    // Shorter effective length — light hits a nearby wall (not the sky)
    const throwLen = (light.beamLength || 0.2) * Math.max(canvas.width, canvas.height) * 0.65;
    const sourceHidden = light.sourceHidden || false;

    let color;
    if (light.customColor) {
      color = hexToRgb(light.customColor);
    } else {
      color = getColorFromTemp(light.colorTemp || 2700);
    }

    const directionRad = (light.direction * Math.PI) / 180;
    const spreadRad = (light.spread * Math.PI) / 180;
    const effectiveSpread = spreadRad * (0.5 + sourceSize * 0.5);

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // Layer 1 — Ambient BOUNCE SPILL: wide soft halo on surrounding surface
    const spillRadius = throwLen * 1.5;
    const spillCX = lightX + Math.sin(directionRad) * (throwLen * 0.45);
    const spillCY = lightY + Math.cos(directionRad) * (throwLen * 0.45);
    const spillGrad = ctx.createRadialGradient(spillCX, spillCY, 0, spillCX, spillCY, spillRadius);
    spillGrad.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.18 * intensity})`);
    spillGrad.addColorStop(0.35, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.09 * intensity})`);
    spillGrad.addColorStop(0.75, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.025 * intensity})`);
    spillGrad.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
    ctx.fillStyle = spillGrad;
    ctx.beginPath();
    ctx.arc(spillCX, spillCY, spillRadius, 0, 2 * Math.PI);
    ctx.fill();

    // Layer 2 — TRIANGULAR WASH FAN: wider than the air-cone, fades fast at far end
    const fanSpread = effectiveSpread * 1.25;
    const fanLayers = 6;
    for (let i = 0; i < fanLayers; i++) {
      const t = i / (fanLayers - 1);
      const layerLen = throwLen * (1 - t * 0.12);
      const layerSpread = fanSpread * (1 - t * 0.18);
      const layerOpacity = intensity * (0.38 - t * 0.045);
      const lAng = directionRad - layerSpread / 2;
      const rAng = directionRad + layerSpread / 2;
      const lX = lightX + Math.sin(lAng) * layerLen;
      const lY = lightY + Math.cos(lAng) * layerLen;
      const rX = lightX + Math.sin(rAng) * layerLen;
      const rY = lightY + Math.cos(rAng) * layerLen;
      const eX = lightX + Math.sin(directionRad) * layerLen;
      const eY = lightY + Math.cos(directionRad) * layerLen;
      const fanGrad = ctx.createLinearGradient(lightX, lightY, eX, eY);
      fanGrad.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${layerOpacity})`);
      fanGrad.addColorStop(0.35, `rgba(${color.r}, ${color.g}, ${color.b}, ${layerOpacity * 0.7})`);
      fanGrad.addColorStop(0.7, `rgba(${color.r}, ${color.g}, ${color.b}, ${layerOpacity * 0.22})`);
      fanGrad.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
      ctx.beginPath();
      ctx.moveTo(lightX, lightY);
      ctx.lineTo(lX, lY);
      ctx.lineTo(rX, rY);
      ctx.closePath();
      ctx.fillStyle = fanGrad;
      ctx.fill();
    }

    // Layer 3 — HOT SPOT at the fixture base (narrow bright blob)
    if (!sourceHidden) {
      const hotLen = throwLen * 0.28;
      const hotSpread = effectiveSpread * 0.55;
      const hotL = directionRad - hotSpread / 2;
      const hotR = directionRad + hotSpread / 2;
      const hLX = lightX + Math.sin(hotL) * hotLen;
      const hLY = lightY + Math.cos(hotL) * hotLen;
      const hRX = lightX + Math.sin(hotR) * hotLen;
      const hRY = lightY + Math.cos(hotR) * hotLen;
      const hEX = lightX + Math.sin(directionRad) * hotLen;
      const hEY = lightY + Math.cos(directionRad) * hotLen;
      const hotGrad = ctx.createLinearGradient(lightX, lightY, hEX, hEY);
      hotGrad.addColorStop(0, `rgba(255, 250, 225, ${Math.min(1, intensity * 1.1)})`);
      hotGrad.addColorStop(0.25, `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity * 0.95})`);
      hotGrad.addColorStop(0.6, `rgba(${color.r}, ${color.g}, ${color.b}, ${intensity * 0.4})`);
      hotGrad.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
      ctx.beginPath();
      ctx.moveTo(lightX, lightY);
      ctx.lineTo(hLX, hLY);
      ctx.lineTo(hEX, hEY);
      ctx.lineTo(hRX, hRY);
      ctx.closePath();
      ctx.fillStyle = hotGrad;
      ctx.fill();

      // Bright fixture core
      const coreR = 7 * sourceSize;
      const coreGrad = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, coreR);
      coreGrad.addColorStop(0, `rgba(255, 255, 240, 1)`);
      coreGrad.addColorStop(0.45, `rgba(${color.r}, ${color.g}, ${color.b}, 0.95)`);
      coreGrad.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(lightX, lightY, coreR, 0, 2 * Math.PI);
      ctx.fill();
    }

    ctx.restore();

    // Fixture indicator dot + direction arrow (match non-wash look)
    if (!sourceHidden) {
      ctx.beginPath();
      const indicatorSize = 3 + sourceSize * 1.5;
      ctx.arc(lightX, lightY, indicatorSize, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, 0.3)`;
      ctx.fill();
      ctx.strokeStyle = `rgba(${Math.floor(color.r * 0.9)}, ${Math.floor(color.g * 0.8)}, ${Math.floor(color.b * 0.6)}, 0.4)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  };

  const drawLight = (ctx, canvas, light) => {
    // Handle neon lights separately
    if (light.beamType === 'neon') {
      drawNeonLight(ctx, canvas, light);
      return;
    }
    
    // Handle grazer lights separately
    if (light.beamType === 'grazer') {
      drawGrazerLight(ctx, canvas, light);
      return;
    }

    // Realistic Wall-Throw mode for beam lights (narrow/medium/wide)
    if (light.surfaceThrow && ['narrow', 'medium', 'wide'].includes(light.beamType)) {
      drawSurfaceThrowBeam(ctx, canvas, light);
      return;
    }
    
    const lightX = (light.position.x / 100) * canvas.width;
    const lightY = (light.position.y / 100) * canvas.height;
    const intensity = light.intensity / 100;
    const sourceSize = light.sourceSize || 1;
    const beamLength = (light.beamLength || 1) * Math.max(canvas.width, canvas.height) * 1.2;
    const sourceHidden = light.sourceHidden || false; // Whether to hide the light source
    
    // Determine color based on whether custom color or preset is used
    let color;
    if (light.customColor) {
      color = hexToRgb(light.customColor);
    } else {
      const colorTemp = light.colorTemp || 2700;
      color = getColorFromTemp(colorTemp);
    }

    // Calculate beam direction in radians
    const directionRad = (light.direction * Math.PI) / 180;
    const spreadRad = (light.spread * Math.PI) / 180;

    // Calculate beam endpoint based on adjustable length
    const endX = lightX + Math.sin(directionRad) * beamLength;
    const endY = lightY + Math.cos(directionRad) * beamLength;

    // Calculate beam cone points - affected by source size
    const effectiveSpread = spreadRad * (0.5 + sourceSize * 0.5);
    const leftAngle = directionRad - effectiveSpread / 2;
    const rightAngle = directionRad + effectiveSpread / 2;

    const leftX = lightX + Math.sin(leftAngle) * beamLength;
    const leftY = lightY + Math.cos(leftAngle) * beamLength;
    const rightX = lightX + Math.sin(rightAngle) * beamLength;
    const rightY = lightY + Math.cos(rightAngle) * beamLength;

    // All beams now emit from a small aperture (~40-50mm real-world
    // equivalent) so the apex looks like a real fixture opening rather
    // than a fake needle-tip cone. Aperture scales with Source Size.
    // The "Hide light source" toggle only controls whether the visible
    // bulb/glow is drawn on top — the beam geometry itself is always
    // trapezoidal from this aperture.
    const apertureRadius = Math.max(3.5, 3.5 * sourceSize + 2);
    const perpAngle = directionRad + Math.PI / 2;
    const apOffX = Math.sin(perpAngle) * apertureRadius;
    const apOffY = Math.cos(perpAngle) * apertureRadius;
    const apLeftX = lightX - apOffX;
    const apLeftY = lightY - apOffY;
    const apRightX = lightX + apOffX;
    const apRightY = lightY + apOffY;

    // Terminal Section — isolate the last N% of the beam and optionally bend
    // it around the knee (aperture-side edge of the isolated section).
    // When terminalRange > 0 we split the trapezoid into base + terminal and
    // rotate the terminal endpoints around the knee midpoint by terminalTilt.
    const terminalRange = Math.max(0, Math.min(100, light.terminalRange || 0));
    const terminalTiltRad = ((light.terminalTilt || 0) * Math.PI) / 180;
    const kneeT = 1 - terminalRange / 100; // 1 = no split, 0 = whole beam is the terminal
    const kneeLeftX = apLeftX + kneeT * (leftX - apLeftX);
    const kneeLeftY = apLeftY + kneeT * (leftY - apLeftY);
    const kneeRightX = apRightX + kneeT * (rightX - apRightX);
    const kneeRightY = apRightY + kneeT * (rightY - apRightY);
    const kneeCX = (kneeLeftX + kneeRightX) / 2;
    const kneeCY = (kneeLeftY + kneeRightY) / 2;
    const rotAroundKnee = (x, y) => {
      const dx = x - kneeCX;
      const dy = y - kneeCY;
      const c = Math.cos(terminalTiltRad);
      const s = Math.sin(terminalTiltRad);
      return { x: kneeCX + dx * c - dy * s, y: kneeCY + dx * s + dy * c };
    };
    const tipLeft = terminalRange > 0 ? rotAroundKnee(leftX, leftY) : { x: leftX, y: leftY };
    const tipRight = terminalRange > 0 ? rotAroundKnee(rightX, rightY) : { x: rightX, y: rightY };

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // Draw directional source glow and core spot - only if source is not hidden
    if (!sourceHidden) {
      // Draw directional source glow (180° arc facing beam direction)
      const glowRadius = 40 * sourceSize;
      
      // Create path for directional glow (semicircle facing beam direction)
      // Arc from directionRad to directionRad + π creates semicircle opening in beam direction
      ctx.beginPath();
      ctx.arc(lightX, lightY, glowRadius, directionRad, directionRad + Math.PI);
      ctx.lineTo(lightX, lightY);
      ctx.closePath();
      
      // Apply realistic gradient with brighter center
      const sourceGradient = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, glowRadius);
      sourceGradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, 1)`);
      sourceGradient.addColorStop(0.2, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.9 * intensity})`);
      sourceGradient.addColorStop(0.5, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.5 * intensity})`);
      sourceGradient.addColorStop(0.8, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.15 * intensity})`);
      sourceGradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
      
      ctx.fillStyle = sourceGradient;
      ctx.fill();
      
      // Add bright core spot for realistic light source
      ctx.beginPath();
      ctx.arc(lightX, lightY, 6 * sourceSize, 0, 2 * Math.PI);
      const coreGradient = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, 6 * sourceSize);
      coreGradient.addColorStop(0, `rgba(255, 255, 220, 1)`);
      coreGradient.addColorStop(0.4, `rgba(${color.r}, ${color.g}, ${color.b}, 1)`);
      coreGradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.3 * intensity})`);
      ctx.fillStyle = coreGradient;
      ctx.fill();
    }

    // Draw main beam cone with realistic volumetric effect
    // Create a unified realistic beam with proper gradients
    
    // Main volumetric beam - single unified cone with realistic falloff
    const beamGradient = ctx.createLinearGradient(lightX, lightY, endX, endY);
    
    // Realistic gradient with sharp center and soft edges
    beamGradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.85 * intensity})`);
    beamGradient.addColorStop(0.15, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.65 * intensity})`);
    beamGradient.addColorStop(0.35, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.4 * intensity})`);
    beamGradient.addColorStop(0.6, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.18 * intensity})`);
    beamGradient.addColorStop(0.8, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.06 * intensity})`);
    beamGradient.addColorStop(0.95, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.01 * intensity})`);
    beamGradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);

    ctx.beginPath();
    if (terminalRange > 0) {
      // Base + terminal quad (bent tip): aperture → knee → tip
      ctx.moveTo(apLeftX, apLeftY);
      ctx.lineTo(kneeLeftX, kneeLeftY);
      ctx.lineTo(tipLeft.x, tipLeft.y);
      ctx.lineTo(tipRight.x, tipRight.y);
      ctx.lineTo(kneeRightX, kneeRightY);
      ctx.lineTo(apRightX, apRightY);
    } else {
      ctx.moveTo(apLeftX, apLeftY);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.lineTo(apRightX, apRightY);
    }
    ctx.closePath();
    ctx.fillStyle = beamGradient;
    ctx.fill();

    // === HAZY EDGE EFFECT - Multiple feathered layers for soft beam edges ===
    // Apply to narrow, medium, and wide beam lights. Each haze layer now
    // follows the same knee-split + tip-bend geometry as the main beam so
    // the total illumination is preserved when the terminal section is active.
    if (['narrow', 'medium', 'wide'].includes(light.beamType)) {
      const hazeSteps = 9; // Number of feather layers (increased further)
      const hazeSpreadMax = effectiveSpread * 0.56; // Another 50% more spread

      for (let i = 1; i <= hazeSteps; i++) {
        const hazeRatio = i / hazeSteps;
        const hazeSpread = effectiveSpread + (hazeSpreadMax * hazeRatio);
        const hazeOpacity = 0.27 * (1 - hazeRatio * 0.55) * intensity;

        const hazeLeftAngle = directionRad - hazeSpread / 2;
        const hazeRightAngle = directionRad + hazeSpread / 2;

        const hazeLeftX = lightX + Math.sin(hazeLeftAngle) * beamLength;
        const hazeLeftY = lightY + Math.cos(hazeLeftAngle) * beamLength;
        const hazeRightX = lightX + Math.sin(hazeRightAngle) * beamLength;
        const hazeRightY = lightY + Math.cos(hazeRightAngle) * beamLength;

        const hazeGradient = ctx.createLinearGradient(lightX, lightY, endX, endY);
        hazeGradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${hazeOpacity})`);
        hazeGradient.addColorStop(0.3, `rgba(${color.r}, ${color.g}, ${color.b}, ${hazeOpacity * 0.6})`);
        hazeGradient.addColorStop(0.6, `rgba(${color.r}, ${color.g}, ${color.b}, ${hazeOpacity * 0.3})`);
        hazeGradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);

        ctx.beginPath();
        if (terminalRange > 0) {
          const hzKneeLX = apLeftX + kneeT * (hazeLeftX - apLeftX);
          const hzKneeLY = apLeftY + kneeT * (hazeLeftY - apLeftY);
          const hzKneeRX = apRightX + kneeT * (hazeRightX - apRightX);
          const hzKneeRY = apRightY + kneeT * (hazeRightY - apRightY);
          const hzTipL = rotAroundKnee(hazeLeftX, hazeLeftY);
          const hzTipR = rotAroundKnee(hazeRightX, hazeRightY);
          ctx.moveTo(apLeftX, apLeftY);
          ctx.lineTo(hzKneeLX, hzKneeLY);
          ctx.lineTo(hzTipL.x, hzTipL.y);
          ctx.lineTo(hzTipR.x, hzTipR.y);
          ctx.lineTo(hzKneeRX, hzKneeRY);
          ctx.lineTo(apRightX, apRightY);
        } else {
          ctx.moveTo(apLeftX, apLeftY);
          ctx.lineTo(hazeLeftX, hazeLeftY);
          ctx.lineTo(hazeRightX, hazeRightY);
          ctx.lineTo(apRightX, apRightY);
        }
        ctx.closePath();
        ctx.fillStyle = hazeGradient;
        ctx.fill();
      }
    }
    // === END HAZY EDGE EFFECT ===

    // Add subtle soft outer glow for atmospheric effect — mirrors the same
    // knee-split geometry so the atmosphere follows the bent tip.
    {
      const outerSpread = effectiveSpread * 1.08;
      const outerLeftAngle = directionRad - outerSpread / 2;
      const outerRightAngle = directionRad + outerSpread / 2;

      const outerLeftX = lightX + Math.sin(outerLeftAngle) * beamLength * 1.02;
      const outerLeftY = lightY + Math.cos(outerLeftAngle) * beamLength * 1.02;
      const outerRightX = lightX + Math.sin(outerRightAngle) * beamLength * 1.02;
      const outerRightY = lightY + Math.cos(outerRightAngle) * beamLength * 1.02;
      const outerEndX = lightX + Math.sin(directionRad) * beamLength * 1.02;
      const outerEndY = lightY + Math.cos(directionRad) * beamLength * 1.02;

      const outerGradient = ctx.createLinearGradient(lightX, lightY, outerEndX, outerEndY);
      outerGradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.15 * intensity})`);
      outerGradient.addColorStop(0.4, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.06 * intensity})`);
      outerGradient.addColorStop(0.75, `rgba(${color.r}, ${color.g}, ${color.b}, ${0.02 * intensity})`);
      outerGradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);

      ctx.beginPath();
      if (terminalRange > 0) {
        const oKneeLX = apLeftX + kneeT * (outerLeftX - apLeftX);
        const oKneeLY = apLeftY + kneeT * (outerLeftY - apLeftY);
        const oKneeRX = apRightX + kneeT * (outerRightX - apRightX);
        const oKneeRY = apRightY + kneeT * (outerRightY - apRightY);
        const oTipL = rotAroundKnee(outerLeftX, outerLeftY);
        const oTipR = rotAroundKnee(outerRightX, outerRightY);
        ctx.moveTo(apLeftX, apLeftY);
        ctx.lineTo(oKneeLX, oKneeLY);
        ctx.lineTo(oTipL.x, oTipL.y);
        ctx.lineTo(oTipR.x, oTipR.y);
        ctx.lineTo(oKneeRX, oKneeRY);
        ctx.lineTo(apRightX, apRightY);
      } else {
        ctx.moveTo(apLeftX, apLeftY);
        ctx.lineTo(outerLeftX, outerLeftY);
        ctx.lineTo(outerRightX, outerRightY);
        ctx.lineTo(apRightX, apRightY);
      }
      ctx.closePath();
      ctx.fillStyle = outerGradient;
      ctx.fill();
    }

    ctx.restore();

    // Draw light source indicator (small subtle dot) - only if source is not hidden
    if (!sourceHidden) {
      ctx.beginPath();
      const indicatorSize = 3 + sourceSize * 1.5;
      ctx.arc(lightX, lightY, indicatorSize, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, 0.3)`;
      ctx.fill();
      ctx.strokeStyle = `rgba(${Math.floor(color.r * 0.9)}, ${Math.floor(color.g * 0.8)}, ${Math.floor(color.b * 0.6)}, 0.4)`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw direction indicator arrow
      const indicatorLength = 12 + sourceSize * 4;
      const indicatorEndX = lightX + Math.sin(directionRad) * indicatorLength;
      const indicatorEndY = lightY + Math.cos(directionRad) * indicatorLength;

      ctx.beginPath();
      ctx.moveTo(lightX, lightY);
      ctx.lineTo(indicatorEndX, indicatorEndY);
      ctx.strokeStyle = `rgba(${Math.floor(color.r * 0.9)}, ${Math.floor(color.g * 0.8)}, ${Math.floor(color.b * 0.6)}, 0.35)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Draw arrowhead
      const arrowSize = 4;
      const arrowAngle1 = directionRad - Math.PI / 6;
      const arrowAngle2 = directionRad + Math.PI / 6;

      ctx.beginPath();
      ctx.moveTo(indicatorEndX, indicatorEndY);
      ctx.lineTo(
        indicatorEndX - Math.sin(arrowAngle1) * arrowSize,
        indicatorEndY - Math.cos(arrowAngle1) * arrowSize
      );
      ctx.moveTo(indicatorEndX, indicatorEndY);
      ctx.lineTo(
        indicatorEndX - Math.sin(arrowAngle2) * arrowSize,
        indicatorEndY - Math.cos(arrowAngle2) * arrowSize
      );
      ctx.strokeStyle = `rgba(${Math.floor(color.r * 0.9)}, ${Math.floor(color.g * 0.8)}, ${Math.floor(color.b * 0.6)}, 0.35)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  };

  const getOccluderAtPosition = (xPct, yPct) => {
    if (!occluders || occluders.length === 0) return null;
    for (let i = occluders.length - 1; i >= 0; i--) {
      const occ = occluders[i];
      if (occ.hidden) continue;
      if (occ.type === 'polygon' && Array.isArray(occ.points) && occ.points.length >= 3) {
        // Point-in-polygon (ray casting)
        const pts = occ.points;
        let inside = false;
        for (let a = 0, b = pts.length - 1; a < pts.length; b = a++) {
          const xi = pts[a].x, yi = pts[a].y;
          const xj = pts[b].x, yj = pts[b].y;
          const intersect = ((yi > yPct) !== (yj > yPct)) &&
            (xPct < ((xj - xi) * (yPct - yi)) / (yj - yi) + xi);
          if (intersect) inside = !inside;
        }
        if (inside) return occ;
      } else {
        const dx = xPct - occ.x;
        const dy = yPct - occ.y;
        const rot = -((occ.rotation || 0) * Math.PI) / 180;
        const lx = dx * Math.cos(rot) - dy * Math.sin(rot);
        const ly = dx * Math.sin(rot) + dy * Math.cos(rot);
        if (Math.abs(lx) <= occ.width / 2 && Math.abs(ly) <= occ.height / 2) {
          return occ;
        }
      }
    }
    return null;
  };

  // Douglas-Peucker polyline simplification for freehand drawing
  const simplifyPath = (points, epsilon) => {
    if (points.length < 3) return points;
    const dist = (p, a, b) => {
      const dx = b.x - a.x, dy = b.y - a.y;
      if (dx === 0 && dy === 0) return Math.hypot(p.x - a.x, p.y - a.y);
      const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
      const ct = Math.max(0, Math.min(1, t));
      return Math.hypot(p.x - (a.x + ct * dx), p.y - (a.y + ct * dy));
    };
    const recur = (pts) => {
      if (pts.length < 3) return pts;
      let maxD = 0, maxI = 0;
      for (let i = 1; i < pts.length - 1; i++) {
        const d = dist(pts[i], pts[0], pts[pts.length - 1]);
        if (d > maxD) { maxD = d; maxI = i; }
      }
      if (maxD > epsilon) {
        const left = recur(pts.slice(0, maxI + 1));
        const right = recur(pts.slice(maxI));
        return [...left.slice(0, -1), ...right];
      }
      return [pts[0], pts[pts.length - 1]];
    };
    return recur(points);
  };

  // ── Neon resize: bbox + 8-handle hit-test ──────────────────────────
  // Returns the axis-aligned bbox in PERCENT coordinates from a neon's path.
  const getNeonBBox = (light) => {
    if (!light || !light.path || light.path.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of light.path) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
  };

  // The 8 handles in % coords given a bbox.
  const getNeonHandlePositions = (bbox) => {
    if (!bbox) return [];
    const { minX, minY, maxX, maxY } = bbox;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    return [
      { handle: 'tl', x: minX, y: minY },
      { handle: 't',  x: cx,   y: minY },
      { handle: 'tr', x: maxX, y: minY },
      { handle: 'r',  x: maxX, y: cy   },
      { handle: 'br', x: maxX, y: maxY },
      { handle: 'b',  x: cx,   y: maxY },
      { handle: 'bl', x: minX, y: maxY },
      { handle: 'l',  x: minX, y: cy   },
    ];
  };

  // Hit-test — returns {light, handle} when the click is within ~5px of a handle.
  const getNeonHandleAtPosition = (xPct, yPct) => {
    if (!selectedLightId) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const light = lights.find(l => l.id === selectedLightId);
    if (!light || light.beamType !== 'neon') return null;
    const bbox = getNeonBBox(light);
    if (!bbox) return null;
    // REDUCED tolerance from 10px to 5px for more precise handle detection
    const tolX = (5 / canvas.width) * 100;
    const tolY = (5 / canvas.height) * 100;
    for (const h of getNeonHandlePositions(bbox)) {
      if (Math.abs(xPct - h.x) <= tolX && Math.abs(yPct - h.y) <= tolY) {
        return { light, handle: h.handle };
      }
    }
    return null;
  };

  // Hit-test corner handle of the selected occluder (rectangles only). Returns {occ, handle} or null.
  const getOccluderHandleAtPosition = (xPct, yPct) => {    if (!selectedOccluderId) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const occ = occluders.find(o => o.id === selectedOccluderId);
    if (!occ || occ.hidden) return null;
    if (occ.type === 'polygon') return null; // no corner handles for polygons
    // Handle radius ~ 8px; convert to % via canvas width
    const radiusPctX = (9 / canvas.width) * 100;
    const radiusPctY = (9 / canvas.height) * 100;
    const rot = ((occ.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const corners = {
      tl: [-occ.width / 2, -occ.height / 2],
      tr: [occ.width / 2, -occ.height / 2],
      br: [occ.width / 2, occ.height / 2],
      bl: [-occ.width / 2, occ.height / 2],
    };
    for (const [handle, [lx, ly]] of Object.entries(corners)) {
      const wx = occ.x + lx * cos - ly * sin;
      const wy = occ.y + lx * sin + ly * cos;
      if (Math.abs(xPct - wx) <= radiusPctX && Math.abs(yPct - wy) <= radiusPctY) {
        return { occ, handle };
      }
    }
    return null;
  };

  // Resize occluder when a corner handle is dragged.
  // The OPPOSITE corner stays fixed in world space.
  const computeOccluderResizeFromHandle = (occ, handle, mxPct, myPct) => {
    const rot = ((occ.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const opposite = { tl: 'br', tr: 'bl', br: 'tl', bl: 'tr' }[handle];
    const cornerLocal = {
      tl: [-occ.width / 2, -occ.height / 2],
      tr: [occ.width / 2, -occ.height / 2],
      br: [occ.width / 2, occ.height / 2],
      bl: [-occ.width / 2, occ.height / 2],
    };
    const [flx, fly] = cornerLocal[opposite];
    const fx = occ.x + flx * cos - fly * sin;
    const fy = occ.y + flx * sin + fly * cos;
    const newCx = (fx + mxPct) / 2;
    const newCy = (fy + myPct) / 2;
    const dx = mxPct - fx;
    const dy = myPct - fy;
    // Inverse rotation: localX = dx*cos + dy*sin, localY = -dx*sin + dy*cos
    const localX = dx * cos + dy * sin;
    const localY = -dx * sin + dy * cos;
    return {
      x: Math.max(0, Math.min(100, newCx)),
      y: Math.max(0, Math.min(100, newCy)),
      width: Math.max(0.5, Math.abs(localX)),
      height: Math.max(0.5, Math.abs(localY)),
    };
  };

  const getLightAtPosition = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const canvasX = ((x - rect.left) / rect.width) * 100;
    const canvasY = ((y - rect.top) / rect.height) * 100;

    // Check if click/touch is near any light
    for (let i = lights.length - 1; i >= 0; i--) {
      const light = lights[i];
      
      // Handle grazer lights (check if click is near the line)
      if (light.beamType === 'grazer' && light.startPoint && light.endPoint) {
        const x1 = light.startPoint.x;
        const y1 = light.startPoint.y;
        const x2 = light.endPoint.x;
        const y2 = light.endPoint.y;
        
        // Calculate distance from point to line segment
        const lineLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        if (lineLength === 0) continue;
        
        const t = Math.max(0, Math.min(1, ((canvasX - x1) * (x2 - x1) + (canvasY - y1) * (y2 - y1)) / (lineLength ** 2)));
        const projX = x1 + t * (x2 - x1);
        const projY = y1 + t * (y2 - y1);
        const distance = Math.sqrt((canvasX - projX) ** 2 + (canvasY - projY) ** 2);
        
        if (distance < 3) { // Close to the line
          return light;
        }
      }
      
      // Handle neon lights (check if click is near the path line segments)
      else if (light.beamType === 'neon' && light.path) {
        // Check if click is near any line segment of the neon path
        for (let i = 0; i < light.path.length - 1; i++) {
          const p1 = light.path[i];
          const p2 = light.path[i + 1];
          
          // Calculate distance from point to line segment
          const lineLength = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
          if (lineLength === 0) continue;
          
          // Calculate projection of click point onto line segment
          const t = Math.max(0, Math.min(1, 
            ((canvasX - p1.x) * (p2.x - p1.x) + (canvasY - p1.y) * (p2.y - p1.y)) / (lineLength ** 2)
          ));
          
          const projX = p1.x + t * (p2.x - p1.x);
          const projY = p1.y + t * (p2.y - p1.y);
          const distance = Math.sqrt((canvasX - projX) ** 2 + (canvasY - projY) ** 2);
          
          // Compact neon hitbox — only trigger when the click is within
          // ~2% of the neon's path. Previously 8% was so greedy that clicks
          // meant for beams behind (or empty space for a new neon) got hijacked.
          if (distance < 2) {
            return light;
          }
        }
      } else if (light.position) {
        // Handle beam lights (check if click is near light source)
        const dx = canvasX - light.position.x;
        const dy = canvasY - light.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 12) {
          return light;
        }
      }
    }
    return null;
  };

  // Get ALL lights within a radius (for touch selection when multiple lights are nearby)
  const getAllLightsInRadius = (x, y, radius = 25) => {
    const canvas = canvasRef.current;
    if (!canvas) return [];

    const rect = canvas.getBoundingClientRect();
    const canvasX = ((x - rect.left) / rect.width) * 100;
    const canvasY = ((y - rect.top) / rect.height) * 100;
    
    const nearbyLights = [];

    for (let i = lights.length - 1; i >= 0; i--) {
      const light = lights[i];
      let distance = Infinity;
      
      if (light.beamType === 'grazer' && light.startPoint && light.endPoint) {
        const x1 = light.startPoint.x;
        const y1 = light.startPoint.y;
        const x2 = light.endPoint.x;
        const y2 = light.endPoint.y;
        const lineLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        if (lineLength > 0) {
          const t = Math.max(0, Math.min(1, ((canvasX - x1) * (x2 - x1) + (canvasY - y1) * (y2 - y1)) / (lineLength ** 2)));
          const projX = x1 + t * (x2 - x1);
          const projY = y1 + t * (y2 - y1);
          distance = Math.sqrt((canvasX - projX) ** 2 + (canvasY - projY) ** 2);
        }
      } else if (light.beamType === 'neon' && light.path) {
        for (const point of light.path) {
          const d = Math.sqrt((canvasX - point.x) ** 2 + (canvasY - point.y) ** 2);
          distance = Math.min(distance, d);
        }
      } else if (light.position) {
        distance = Math.sqrt((canvasX - light.position.x) ** 2 + (canvasY - light.position.y) ** 2);
      }
      
      if (distance < radius) {
        nearbyLights.push({ light, distance });
      }
    }
    
    // Sort by distance (closest first)
    nearbyLights.sort((a, b) => a.distance - b.distance);
    return nearbyLights.map(item => item.light);
  };

  // Get light label for display in popup
  const getLightLabel = (light, index) => {
    const lightIndex = lights.findIndex(l => l.id === light.id) + 1;
    if (light.beamType === 'neon') {
      return `${lightIndex}. Neon ${light.shapeType || 'freeform'}`;
    } else if (light.beamType === 'grazer') {
      return `${lightIndex}. Grazer`;
    } else {
      return `${lightIndex}. ${light.beamType.charAt(0).toUpperCase() + light.beamType.slice(1)} Beam`;
    }
  };

  // Helper to detect which part of grazer was clicked
  const getGrazerPartAtPosition = (x, y, light) => {
    if (light.beamType !== 'grazer' || !light.startPoint || !light.endPoint) return null;
    
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = ((x - rect.left) / rect.width) * 100;
    const canvasY = ((y - rect.top) / rect.height) * 100;
    
    // Check if clicking start handle
    const distStart = Math.sqrt(
      Math.pow(canvasX - light.startPoint.x, 2) + 
      Math.pow(canvasY - light.startPoint.y, 2)
    );
    if (distStart < 4) return 'start';
    
    // Check if clicking end handle
    const distEnd = Math.sqrt(
      Math.pow(canvasX - light.endPoint.x, 2) + 
      Math.pow(canvasY - light.endPoint.y, 2)
    );
    if (distEnd < 4) return 'end';
    
    // Check if clicking on the line
    const x1 = light.startPoint.x;
    const y1 = light.startPoint.y;
    const x2 = light.endPoint.x;
    const y2 = light.endPoint.y;
    
    const lineLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    if (lineLength === 0) return null;
    
    const t = Math.max(0, Math.min(1, ((canvasX - x1) * (x2 - x1) + (canvasY - y1) * (y2 - y1)) / (lineLength ** 2)));
    const projX = x1 + t * (x2 - x1);
    const projY = y1 + t * (y2 - y1);
    const distance = Math.sqrt((canvasX - projX) ** 2 + (canvasY - projY) ** 2);
    
    if (distance < 3) return 'line';
    
    return null;
  };

  // Detect shape from drawn points
  const detectShape = (points) => {
    if (points.length < 3) return null;
    
    const first = points[0];
    const last = points[points.length - 1];
    
    // Check if it's a line (start and end far apart, points roughly collinear)
    const isLine = () => {
      const dx = last.x - first.x;
      const dy = last.y - first.y;
      const lineLength = Math.sqrt(dx * dx + dy * dy);
      
      if (lineLength < 3) return false; // Too short
      
      // Check if points are roughly on a line
      let maxDeviation = 0;
      for (const point of points) {
        const distance = Math.abs(
          (dy * point.x - dx * point.y + last.x * first.y - last.y * first.x) / lineLength
        );
        maxDeviation = Math.max(maxDeviation, distance);
      }
      
      return maxDeviation < 5; // Allow 5% deviation (more forgiving)
    };
    
    // Check if it's a circle (closed loop, roughly circular)
    const isCircle = () => {
      const closingDist = Math.sqrt(
        Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2)
      );
      
      if (closingDist > 5) return false; // Not closed
      
      // Find center (average of all points)
      const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
      const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
      
      // Check if all points are roughly equidistant from center
      const distances = points.map(p => 
        Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.y - centerY, 2))
      );
      const avgRadius = distances.reduce((sum, d) => sum + d, 0) / distances.length;
      const maxDeviation = Math.max(...distances.map(d => Math.abs(d - avgRadius)));
      
      if (avgRadius < 3) return false; // Too small
      
      return maxDeviation / avgRadius < 0.2; // Allow 20% radius variation
    };
    
    // Check if it's a rectangle/square (4 corners, closed)
    const isRectangle = () => {
      const closingDist = Math.sqrt(
        Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2)
      );
      
      if (closingDist > 5) return false; // Not closed
      
      // Find approximate corners (points where direction changes significantly)
      const corners = [points[0]];
      for (let i = 1; i < points.length - 1; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1];
        
        const angle1 = Math.atan2(curr.y - prev.y, curr.x - prev.x);
        const angle2 = Math.atan2(next.y - curr.y, next.x - curr.x);
        let angleDiff = Math.abs(angle2 - angle1);
        if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
        
        if (angleDiff > Math.PI / 4) { // Significant direction change
          corners.push(curr);
        }
      }
      
      // Should have 4 corners for rectangle
      if (corners.length >= 3 && corners.length <= 5) {
        return { type: 'rectangle', corners: corners.slice(0, 4) };
      }
      
      return false;
    };
    
    // Try to detect shapes
    if (isLine()) {
      return { type: 'line', start: first, end: last };
    }
    
    if (isCircle()) {
      const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
      const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
      const distances = points.map(p => 
        Math.sqrt(Math.pow(p.x - centerX, 2) + Math.pow(p.y - centerY, 2))
      );
      const radius = distances.reduce((sum, d) => sum + d, 0) / distances.length;
      
      return { type: 'circle', center: { x: centerX, y: centerY }, radius };
    }
    
    const rectResult = isRectangle();
    if (rectResult) {
      return rectResult;
    }
    
    return null;
  };

  // Convert detected shape to path points
  const shapeToPath = (shape) => {
    if (!shape) return null;
    
    if (shape.type === 'line') {
      return [shape.start, shape.end];
    }
    
    if (shape.type === 'circle') {
      const points = [];
      const numPoints = 50;
      for (let i = 0; i <= numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI;
        points.push({
          x: shape.center.x + Math.cos(angle) * shape.radius,
          y: shape.center.y + Math.sin(angle) * shape.radius
        });
      }
      return points;
    }
    
    if (shape.type === 'rectangle') {
      return [...shape.corners, shape.corners[0]]; // Close the shape
    }
    
    return null;
  };

  const handleMouseDown = (event) => {
    event.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Handle panning when space is pressed and zoomed in
    if (spacePressed && zoomLevel > 1) {
      if (setIsPanning) setIsPanning(true);
      if (panStartRef) panStartRef.current = { x: event.clientX, y: event.clientY };
      if (lastPanOffsetRef) lastPanOffsetRef.current = { ...panOffset };
      return;
    }
    const rect = canvas.getBoundingClientRect();
    // Adjust for zoom when calculating click position
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clickX = ((event.clientX - rect.left) * scaleX / canvas.width) * 100;
    const clickY = ((event.clientY - rect.top) * scaleY / canvas.height) * 100;
    
    // Handle occluder drawing mode — freehand polygon capture
    if (isDrawingOccluder) {
      setOccluderDrawPath([{ x: clickX, y: clickY }]);
      setIsDragging(true); // reuse flag so we know we're capturing
      return;
    }

    // Handle corner handle of selected occluder (resize)
    if (!previewMode) {
      // Check neon resize handles FIRST with tight tolerance (5px)
      // This allows precise clicking on handles to resize
      const neonHit = getNeonHandleAtPosition(clickX, clickY);
      if (neonHit) {
        console.log('[RESIZE] Neon handle hit:', neonHit.handle);
        setDraggedNeonId(neonHit.light.id);
        setDraggedNeonHandle(neonHit.handle);
        draggedNeonStartRef.current = {
          path: neonHit.light.path.map(p => ({ ...p })),
          bbox: getNeonBBox(neonHit.light),
          shiftKey: !!event.shiftKey,
        };
        return;
      }
      const handleHit = getOccluderHandleAtPosition(clickX, clickY);
      if (handleHit) {
        setDraggedOccluderId(handleHit.occ.id);
        setDraggedOccluderHandle(handleHit.handle);
        return;
      }
    }

    // Handle neon drawing mode
    if (isDrawingNeon) {
      setIsDrawing(true);
      setNeonDrawPoints([{ x: clickX, y: clickY }]);
      setShowMagnifier(true);
      setMagnifierPos({ x: event.clientX, y: event.clientY });
      return;
    }
    
    // PRIORITY FIX: If an occluder is already selected, check occluder area FIRST
    // This prevents neon lights from being selected when trying to drag the occluder
    if (selectedOccluderId && !previewMode) {
      const selectedOcc = (occluders || []).find(o => o.id === selectedOccluderId);
      if (selectedOcc) {
        // Check if clicking inside selected occluder's bounding box
        const occAtPos = getOccluderAtPosition(clickX, clickY);
        if (occAtPos && occAtPos.id === selectedOccluderId) {
          // Clicking on the already-selected occluder → drag it
          setDraggedOccluderId(occAtPos.id);
          setIsDraggingOccluder(true);
          return;
        }
      }
    }
    
    // Check if clicking on any logo (not tiled)
    for (const logo of logos) {
      if (!logo.tiled) {
        const logoImg = logoImagesRef.current[logo.id];
        if (!logoImg) continue;
        
        const logoWidth = logo.size;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        
        const dx = Math.abs(clickX - logo.position.x);
        const dy = Math.abs(clickY - logo.position.y);
        
        if (dx < logoWidth / 2 && dy < logoHeight / 2) {
          // Select and start dragging this logo
          if (onLogoSelect) onLogoSelect(logo.id);
          setDraggedLogoId(logo.id);
          setIsDraggingLogo(true);
          return;
        }
      }
    }
    
    // PRIORITY: Check for occluder FIRST (before lights) to prevent neon from blocking occluder interaction
    if (!previewMode) {
      const occAtPos = getOccluderAtPosition(clickX, clickY);
      if (occAtPos) {
        console.log('[OCCLUDER] Detected at click position:', occAtPos.id);
        // Select the occluder
        if (onOccluderSelect) onOccluderSelect(occAtPos.id);
        
        // If clicking on already-selected occluder, start dragging
        if (occAtPos.id === selectedOccluderId) {
          console.log('[OCCLUDER] Starting drag for selected occluder');
          setDraggedOccluderId(occAtPos.id);
          setIsDraggingOccluder(true);
        }
        return;
      }
    }
    
    // If no occluder, then check for light
    let lightAtPos = getLightAtPosition(event.clientX, event.clientY);
    // Bbox-interior fallback for the SELECTED neon — lets users grab thin
    // neon lines from anywhere inside their cyan dashed bbox (Figma-style).
    if (!lightAtPos && selectedLightId) {
      const sel = lights.find(l => l.id === selectedLightId);
      if (sel && sel.beamType === 'neon') {
        const bb = getNeonBBox(sel);
        if (bb && clickX >= bb.minX && clickX <= bb.maxX && clickY >= bb.minY && clickY <= bb.maxY) {
          lightAtPos = sel;
        }
      }
    }
    if (lightAtPos) {
      // IMPORTANT: Only allow dragging if the light is already selected
      // First click should just select, second click/drag should move
      if (lightAtPos.id !== selectedLightId) {
        // Light not selected - select it first, don't start dragging
        if (onLightSelect) {
          onLightSelect(lightAtPos.id);
        }
        return;
      }
      
      // Light is already selected - allow dragging
      // Check if it's a grazer light
      if (lightAtPos.beamType === 'grazer') {
        const part = getGrazerPartAtPosition(event.clientX, event.clientY, lightAtPos);
        if (part) {
          setIsDragging(true);
          setDraggedLightId(lightAtPos.id);
          setDraggedGrazerPart(part);
          return;
        }
      }
      // Neon translate via bbox-interior drag — snapshot path + start cursor
      if (lightAtPos.beamType === 'neon' && lightAtPos.path) {
        console.log('[NEON DRAG] Initiating neon drag for light', lightAtPos.id, 'at', clickX, clickY);
        setIsDragging(true);
        setDraggedLightId(lightAtPos.id);
        draggedNeonMoveRef.current = {
          path: lightAtPos.path.map(p => ({ ...p })),
          startX: clickX,
          startY: clickY,
        };
        console.log('[NEON DRAG] draggedNeonMoveRef set:', draggedNeonMoveRef.current);
        return;
      }
      // Regular light dragging
      setIsDragging(true);
      setDraggedLightId(lightAtPos.id);
    } else {
      // Clicked on empty space - deselect current light/occluder
      if (onLightSelect && selectedLightId) {
        onLightSelect(null);
      }
      if (onOccluderSelect && selectedOccluderId) {
        onOccluderSelect(null);
      }
    }
  };

  const handleMouseMove = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Handle panning
    if (isPanning && spacePressed && zoomLevel > 1 && onPanChange && panStartRef && lastPanOffsetRef) {
      const dx = event.clientX - panStartRef.current.x;
      const dy = event.clientY - panStartRef.current.y;
      onPanChange({
        x: lastPanOffsetRef.current.x + dx,
        y: lastPanOffsetRef.current.y + dy
      });
      return;
    }
    
    const rect = canvas.getBoundingClientRect();
    // Adjust for zoom when calculating position
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((event.clientX - rect.left) * scaleX / canvas.width) * 100;
    const y = ((event.clientY - rect.top) * scaleY / canvas.height) * 100;
    
    // Handle neon drawing
    if (isDrawing && isDrawingNeon) {
      event.preventDefault();
      
      // Update magnifier position
      setMagnifierPos({ x: event.clientX, y: event.clientY });
      
      // Add point to drawing path
      const lastPoint = neonDrawPoints[neonDrawPoints.length - 1];
      const distance = Math.sqrt(Math.pow(x - lastPoint.x, 2) + Math.pow(y - lastPoint.y, 2));
      
      if (distance > 0.5) {
        setNeonDrawPoints([...neonDrawPoints, { x, y }]);
      }
      return;
    }

    // Occluder freehand drawing — append points while dragging
    if (isDrawingOccluder && occluderDrawPath.length > 0) {
      event.preventDefault();
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));
      const last = occluderDrawPath[occluderDrawPath.length - 1];
      if (Math.hypot(clampedX - last.x, clampedY - last.y) > 0.25) {
        setOccluderDrawPath([...occluderDrawPath, { x: clampedX, y: clampedY }]);
      }
      return;
    }

    // Neon resize via 8-handle drag — geometric scale around the opposite handle
    if (draggedNeonHandle && draggedNeonId && onNeonResize && draggedNeonStartRef.current) {
      event.preventDefault();
      const { path: startPath, bbox, shiftKey } = draggedNeonStartRef.current;
      const handle = draggedNeonHandle;
      const cx = (bbox.minX + bbox.maxX) / 2;
      const cy = (bbox.minY + bbox.maxY) / 2;
      const useCenter = shiftKey || event.shiftKey;
      let ax, ay;
      if (useCenter) {
        ax = cx; ay = cy;
      } else {
        ax = handle.includes('l') ? bbox.maxX : handle.includes('r') ? bbox.minX : cx;
        ay = handle.includes('t') ? bbox.maxY : handle.includes('b') ? bbox.minY : cy;
      }
      const mx = Math.max(0, Math.min(100, x));
      const my = Math.max(0, Math.min(100, y));
      const scaleXEnabled = handle.includes('l') || handle.includes('r');
      const scaleYEnabled = handle.includes('t') || handle.includes('b');
      const handlePos = getNeonHandlePositions(bbox).find(h => h.handle === handle);
      let sx = 1, sy = 1;
      if (scaleXEnabled) {
        const denom = handlePos.x - ax;
        if (Math.abs(denom) > 0.001) sx = (mx - ax) / denom;
      }
      if (scaleYEnabled) {
        const denom = handlePos.y - ay;
        if (Math.abs(denom) > 0.001) sy = (my - ay) / denom;
      }
      // Corner handles default to aspect-locked unless Alt held
      if (scaleXEnabled && scaleYEnabled && !event.altKey) {
        const s = Math.min(Math.abs(sx), Math.abs(sy));
        sx = Math.sign(sx || 1) * s;
        sy = Math.sign(sy || 1) * s;
      }
      const MIN_S = 0.1, MAX_S = 5;
      sx = Math.max(MIN_S, Math.min(MAX_S, sx || 1));
      sy = Math.max(MIN_S, Math.min(MAX_S, sy || 1));
      const newPath = startPath.map(p => ({
        ...p,
        x: ax + (p.x - ax) * sx,
        y: ay + (p.y - ay) * sy,
      }));
      onNeonResize(draggedNeonId, newPath);
      return;
    }

    // Occluder corner-handle resize
    if (draggedOccluderHandle && draggedOccluderId && onOccluderResize) {
      event.preventDefault();
      const occ = occluders.find(o => o.id === draggedOccluderId);
      if (occ) {
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));
        const newBounds = computeOccluderResizeFromHandle(occ, draggedOccluderHandle, clampedX, clampedY);
        onOccluderResize(draggedOccluderId, newBounds);
      }
      return;
    }

    if (isDraggingLogo && draggedLogoId && onLogoPositionChange) {
      event.preventDefault();
      // Clamp to canvas bounds
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      onLogoPositionChange(draggedLogoId, { x: clampedX, y: clampedY });
    } else if (isDragging && draggedLightId) {
      event.preventDefault();
      
      // Neon translate via bbox-interior drag — translate the entire path by mouse delta
      if (draggedNeonMoveRef.current && onNeonPathUpdate) {
        const { path: startPath, startX, startY } = draggedNeonMoveRef.current;
        const dx = x - startX;
        const dy = y - startY;
        console.log('[NEON DRAG] Moving neon, delta:', dx, dy);
        const newPath = startPath.map(p => ({
          ...p,
          x: Math.max(0, Math.min(100, p.x + dx)),
          y: Math.max(0, Math.min(100, p.y + dy)),
        }));
        onNeonPathUpdate(draggedLightId, newPath);
        return;
      }
      
      // Check if dragging a grazer light
      if (draggedGrazerPart && onGrazerDrag) {
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));
        onGrazerDrag(draggedLightId, draggedGrazerPart, clampedX, clampedY);
      } else {
        // Regular light dragging
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));
        onLightDrag(draggedLightId, clampedX, clampedY);
      }
    } else if (isDraggingOccluder && draggedOccluderId && onOccluderPositionChange) {
      event.preventDefault();
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));
      onOccluderPositionChange(draggedOccluderId, clampedX, clampedY);
    }
  };

  const handleMouseUp = (event) => {
    // End panning
    if (isPanning && setIsPanning) {
      setIsPanning(false);
      return;
    }
    // Clear neon-resize state on any mouseup
    if (draggedNeonId || draggedNeonHandle) {
      setDraggedNeonId(null);
      setDraggedNeonHandle(null);
      draggedNeonStartRef.current = null;
    }
    
    if (isDragging || isDraggingLogo) {
      event.preventDefault();
    }
    
    // Complete neon drawing
    if (isDrawing && isDrawingNeon && neonDrawPoints.length > 0) {
      event.preventDefault();
      
      if (onNeonDrawingComplete) {
        onNeonDrawingComplete(neonDrawPoints);
      }
      
      setIsDrawing(false);
      setNeonDrawPoints([]);
      setShowMagnifier(false);
      return;
    }
    
    // Complete occluder drawing (mouse up) — freehand polygon
    if (isDrawingOccluder && occluderDrawPath.length > 2) {
      event.preventDefault();
      const simplified = simplifyPath(occluderDrawPath, 0.6);
      const pts = simplified.length >= 3 ? simplified : occluderDrawPath;
      if (pts.length >= 3 && onOccluderDrawingComplete) {
        onOccluderDrawingComplete({ type: 'polygon', points: pts });
      }
      setOccluderDrawPath([]);
      setIsDragging(false);
      return;
    }
    // Cancel drawing if too few points
    if (isDrawingOccluder) {
      setOccluderDrawPath([]);
      setIsDragging(false);
      return;
    }

    setIsDragging(false);
    setDraggedLightId(null);
    setIsDraggingLogo(false);
    setDraggedLogoId(null);
    setDraggedGrazerPart(null);
    setIsDraggingOccluder(false);
    setDraggedOccluderId(null);
    setDraggedOccluderHandle(null);
    draggedNeonMoveRef.current = null;
  };

  // Touch event handlers for mobile devices
  const handleTouchStart = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Handle two-finger gestures (pinch zoom / pan)
    if (event.touches.length === 2) {
      const dist = getTouchDistance(event.touches);
      const center = getTouchCenter(event.touches);
      setTouchStartDist(dist);
      setTouchStartZoom(zoomLevel);
      setTouchPanStart(center);
      if (lastPanOffsetRef) lastPanOffsetRef.current = { ...panOffset };
      setIsTouchPanning(true);
      return;
    }
    
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      
      const rect = canvas.getBoundingClientRect();
      // Adjust for zoom
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const touchX = ((touch.clientX - rect.left) * scaleX / canvas.width) * 100;
      const touchY = ((touch.clientY - rect.top) * scaleY / canvas.height) * 100;

      // Occluder freehand drawing mode (touch)
      if (isDrawingOccluder) {
        setOccluderDrawPath([{ x: touchX, y: touchY }]);
        setIsDragging(true);
        return;
      }

      // Occluder corner-handle hit-test (touch)
      if (!previewMode) {
        const handleHit = getOccluderHandleAtPosition(touchX, touchY);
        if (handleHit) {
          setDraggedOccluderId(handleHit.occ.id);
          setDraggedOccluderHandle(handleHit.handle);
          return;
        }
      }

      // ═══════════════════════════════════════════════════════════════
      // PRIORITY 1 (touch): Neon drawing mode ALWAYS wins.
      // Otherwise, tapping to start a new neon right next to a
      // just-created (auto-selected) neon would grab the old one instead.
      // ═══════════════════════════════════════════════════════════════
      if (isDrawingNeon) {
        setIsDrawing(true);
        setNeonDrawPoints([{ x: touchX, y: touchY }]);
        setShowMagnifier(true);
        setMagnifierPos({ x: touch.clientX, y: touch.clientY });
        return;
      }

      // ═══════════════════════════════════════════════════════════════
      // PRIORITY 2: Check if user is trying to drag EXISTING selected neon
      // ═══════════════════════════════════════════════════════════════

      // Check for light — compact touch radius so nearby-but-not-on lights
      // don't get hijacked. Previously 40% was so wide that tapping
      // anywhere in the middle of the canvas grabbed a neon.
      const touchRadius = 6;
      let nearbyLights = getAllLightsInRadius(touch.clientX, touch.clientY, touchRadius);
      
      // Bbox-interior fallback for SELECTED neon on touch
      if (nearbyLights.length === 0 && selectedLightId) {
        const sel = lights.find(l => l.id === selectedLightId);
        if (sel && sel.beamType === 'neon') {
          const bb = getNeonBBox(sel);
          if (bb && touchX >= bb.minX && touchX <= bb.maxX && touchY >= bb.minY && touchY <= bb.maxY) {
            console.log('[TOUCH] Neon bbox interior hit for selected neon', sel.id);
            nearbyLights = [sel];
          }
        }
      }
      
      // If touching an existing light (especially a selected neon), handle it first
      if (nearbyLights.length >= 1) {
        const lightAtPos = nearbyLights[0];
        
        // If touching the already-selected neon, initiate drag (don't start drawing!)
        if (lightAtPos.id === selectedLightId && lightAtPos.beamType === 'neon' && lightAtPos.path) {
          console.log('[TOUCH] Initiating neon drag for SELECTED light', lightAtPos.id);
          setIsDragging(true);
          setDraggedLightId(lightAtPos.id);
          draggedNeonMoveRef.current = {
            path: lightAtPos.path.map(p => ({ ...p })),
            startX: touchX,
            startY: touchY,
          };
          return; // Exit early - don't enter drawing mode!
        }
        
        // Otherwise, handle selection/dragging of other lights below
      }

      // ═══════════════════════════════════════════════════════════════
      // PRIORITY 3: Handle neon drawing mode (only if not dragging selected neon)
      // Note: drawing-mode is also handled at PRIORITY 1 above, so this is a fallback.
      // ═══════════════════════════════════════════════════════════════
      if (isDrawingNeon) {
        setIsDrawing(true);
        setNeonDrawPoints([{ x: touchX, y: touchY }]);
        setShowMagnifier(true);
        setMagnifierPos({ x: touch.clientX, y: touch.clientY });
        return;
      }
      
      // Check if touching any logo (not tiled)
      for (const logo of logos) {
        if (!logo.tiled) {
          const logoImg = logoImagesRef.current[logo.id];
          if (!logoImg) continue;
          
          const logoWidth = logo.size;
          const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
          
          const dx = Math.abs(touchX - logo.position.x);
          const dy = Math.abs(touchY - logo.position.y);
          
          if (dx < logoWidth / 2 && dy < logoHeight / 2) {
            if (onLogoSelect) onLogoSelect(logo.id);
            setDraggedLogoId(logo.id);
            setIsDraggingLogo(true);
            return;
          }
        }
      }
      
      // Handle remaining light interactions (if not already handled above)
      if (nearbyLights.length >= 1) {
        const lightAtPos = nearbyLights[0];
        
        // If light not selected yet, select it first
        if (lightAtPos.id !== selectedLightId) {
          if (onLightSelect) {
            onLightSelect(lightAtPos.id);
          }
          return;
        }
        
        // Light already selected - allow dragging
        // Handle grazer lights
        if (lightAtPos.beamType === 'grazer') {
          const part = getGrazerPartAtPosition(touch.clientX, touch.clientY, lightAtPos);
          if (part) {
            setIsDragging(true);
            setDraggedLightId(lightAtPos.id);
            setDraggedGrazerPart(part);
            return;
          }
        }
        
        // Handle other neon lights (non-selected or selection via path, not bbox)
        if (lightAtPos.beamType === 'neon' && lightAtPos.path) {
          console.log('[TOUCH] Initiating neon drag for light (path hit)', lightAtPos.id);
          setIsDragging(true);
          setDraggedLightId(lightAtPos.id);
          draggedNeonMoveRef.current = {
            path: lightAtPos.path.map(p => ({ ...p })),
            startX: touchX,
            startY: touchY,
          };
          console.log('[TOUCH] draggedNeonMoveRef set:', draggedNeonMoveRef.current);
          return;
        }
        
        // Regular light dragging
        setIsDragging(true);
        setDraggedLightId(lightAtPos.id);
      } else {
        // No light — check occluder (edit mode only)
        if (!previewMode) {
          const occAtPos = getOccluderAtPosition(touchX, touchY);
          if (occAtPos) {
            if (onOccluderSelect) onOccluderSelect(occAtPos.id);
            setDraggedOccluderId(occAtPos.id);
            setIsDraggingOccluder(true);
            return;
          }
        }
        // Touched on empty space - deselect current light/occluder
        if (onLightSelect && selectedLightId) {
          onLightSelect(null);
        }
        if (onOccluderSelect && selectedOccluderId) {
          onOccluderSelect(null);
        }
      }
    }
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Handle two-finger gestures (pinch zoom / pan)
    if (event.touches.length === 2 && isTouchPanning) {
      const dist = getTouchDistance(event.touches);
      const center = getTouchCenter(event.touches);
      
      // Pinch to zoom
      if (touchStartDist && dist) {
        const scale = dist / touchStartDist;
        const newZoom = Math.min(Math.max(touchStartZoom * scale, 1), 4);
        
        // Update zoom through parent
        if (window.setZoomLevelFromCanvas) {
          window.setZoomLevelFromCanvas(newZoom);
        }
      }
      
      // Two-finger pan
      if (touchPanStart && center && onPanChange && lastPanOffsetRef) {
        const dx = center.x - touchPanStart.x;
        const dy = center.y - touchPanStart.y;
        onPanChange({
          x: lastPanOffsetRef.current.x + dx,
          y: lastPanOffsetRef.current.y + dy
        });
      }
      return;
    }
    
    if (event.touches.length > 0) {
      const touch = event.touches[0];

      const rect = canvas.getBoundingClientRect();
      // Adjust for zoom
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = ((touch.clientX - rect.left) * scaleX / canvas.width) * 100;
      const y = ((touch.clientY - rect.top) * scaleY / canvas.height) * 100;
      
      // Handle neon drawing
      if (isDrawing && isDrawingNeon) {
        // Update magnifier position
        setMagnifierPos({ x: touch.clientX, y: touch.clientY });
        
        const lastPoint = neonDrawPoints[neonDrawPoints.length - 1];
        const distance = Math.sqrt(Math.pow(x - lastPoint.x, 2) + Math.pow(y - lastPoint.y, 2));
        
        if (distance > 0.5) {
          setNeonDrawPoints([...neonDrawPoints, { x, y }]);
        }
        return;
      }

      // Occluder drawing preview (touch) - add points to path
      if (isDrawingOccluder && occluderDrawPath.length > 0) {
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));
        const lastPoint = occluderDrawPath[occluderDrawPath.length - 1];
        const distance = Math.sqrt((x - lastPoint.x) ** 2 + (y - lastPoint.y) ** 2);
        
        // Only add point if moved enough (reduce jitter)
        if (distance > 1.0) {
          setOccluderDrawPath([...occluderDrawPath, { x: clampedX, y: clampedY }]);
        }
        return;
      }

      // Occluder corner-handle resize (touch)
      if (draggedOccluderHandle && draggedOccluderId && onOccluderResize) {
        const occ = occluders.find(o => o.id === draggedOccluderId);
        if (occ) {
          const clampedX = Math.max(0, Math.min(100, x));
          const clampedY = Math.max(0, Math.min(100, y));
          const newBounds = computeOccluderResizeFromHandle(occ, draggedOccluderHandle, clampedX, clampedY);
          onOccluderResize(draggedOccluderId, newBounds);
        }
        return;
      }

      if (isDraggingLogo && draggedLogoId && onLogoPositionChange) {
        // Clamp to canvas bounds
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));

        onLogoPositionChange(draggedLogoId, { x: clampedX, y: clampedY });
      } else if (isDragging && draggedLightId) {
        // Neon translate via bbox-interior touch
        if (draggedNeonMoveRef.current && onNeonPathUpdate) {
          const { path: startPath, startX, startY } = draggedNeonMoveRef.current;
          const dx = x - startX;
          const dy = y - startY;
          console.log('[TOUCH] Moving neon, delta:', dx, dy);
          const newPath = startPath.map(p => ({
            ...p,
            x: Math.max(0, Math.min(100, p.x + dx)),
            y: Math.max(0, Math.min(100, p.y + dy)),
          }));
          onNeonPathUpdate(draggedLightId, newPath);
          return;
        }
        // Check if dragging a grazer light
        if (draggedGrazerPart && onGrazerDrag) {
          const clampedX = Math.max(0, Math.min(100, x));
          const clampedY = Math.max(0, Math.min(100, y));
          onGrazerDrag(draggedLightId, draggedGrazerPart, clampedX, clampedY);
        } else {
          // Regular light dragging
          const clampedX = Math.max(0, Math.min(100, x));
          const clampedY = Math.max(0, Math.min(100, y));
          onLightDrag(draggedLightId, clampedX, clampedY);
        }
      } else if (isDraggingOccluder && draggedOccluderId && onOccluderPositionChange) {
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));
        onOccluderPositionChange(draggedOccluderId, clampedX, clampedY);
      }
    }
  };

  const handleTouchEnd = (event) => {
    // Reset touch gesture state
    if (isTouchPanning) {
      setIsTouchPanning(false);
      setTouchStartDist(null);
      setTouchPanStart(null);
      return;
    }
    
    if (isDragging || isDraggingLogo) {
      event.preventDefault();
    }
    
    // Complete neon drawing
    if (isDrawing && isDrawingNeon && neonDrawPoints.length > 0) {
      event.preventDefault();
      
      if (onNeonDrawingComplete) {
        onNeonDrawingComplete(neonDrawPoints);
      }
      
      setIsDrawing(false);
      setNeonDrawPoints([]);
      setShowMagnifier(false);
      return;
    }
    
    // Complete occluder drawing (touch end) — freehand polygon
    if (isDrawingOccluder && occluderDrawPath.length > 2) {
      event.preventDefault();
      const simplified = simplifyPath(occluderDrawPath, 0.6);
      const pts = simplified.length >= 3 ? simplified : occluderDrawPath;
      if (pts.length >= 3 && onOccluderDrawingComplete) {
        onOccluderDrawingComplete({ type: 'polygon', points: pts });
      }
      setOccluderDrawPath([]);
      setIsDragging(false);
      return;
    }
    if (isDrawingOccluder) {
      setOccluderDrawPath([]);
      setIsDragging(false);
      return;
    }

    setIsDragging(false);
    setDraggedLightId(null);
    setIsDraggingLogo(false);
    setDraggedLogoId(null);
    setDraggedGrazerPart(null);
    setIsDraggingOccluder(false);
    setDraggedOccluderId(null);
    setDraggedOccluderHandle(null);
  };

  return (
    <div 
      ref={containerRef} 
      className="relative overflow-hidden flex items-center justify-center"
      style={{
        cursor: spacePressed ? (isPanning ? 'grabbing' : 'grab') : 'default'
      }}
    >
      <div
        style={{
          transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
          transformOrigin: 'center center',
          transition: isPanning ? 'none' : 'transform 0.1s ease-out',
          display: 'inline-block',
          lineHeight: 0,
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="rounded-lg touch-none"
          style={{ 
            display: 'block',
            margin: '0 auto',
            cursor: spacePressed 
              ? (isPanning ? 'grabbing' : 'grab')
              : isDrawingOccluder
                ? 'crosshair'
                : isDrawingNeon 
                  ? 'crosshair' 
                  : (isDragging ? 'grabbing' : 'grab')
          }}
        />
      </div>
      
      {/* Magnifier Lens - Realistic Glass Effect */}
      {showMagnifier && magnifierPos.x > 0 && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: magnifierPos.x + 50,
            top: magnifierPos.y - 100,
            width: '180px',
            height: '180px',
            zIndex: 1000
          }}
        >
          {/* Outer shadow ring */}
          <div className="absolute inset-0 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(0,0,0,0) 60%, rgba(0,0,0,0.4) 100%)',
            filter: 'blur(8px)'
          }}></div>
          
          {/* Glass lens frame */}
          <div className="absolute inset-0 rounded-full" style={{
            background: 'linear-gradient(135deg, rgba(100,100,100,0.3) 0%, rgba(60,60,60,0.5) 100%)',
            border: '3px solid rgba(80,80,80,0.6)',
            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.5)'
          }}></div>
          
          {/* Inner glass surface */}
          <div className="absolute rounded-full overflow-hidden" style={{
            top: '10px',
            left: '10px',
            right: '10px',
            bottom: '10px',
            border: '2px solid rgba(34, 211, 238, 0.6)',
            boxShadow: 'inset 0 0 20px rgba(34, 211, 238, 0.2), 0 0 20px rgba(34, 211, 238, 0.3)'
          }}>
            <canvas
              ref={magnifierCanvasRef}
              width={160}
              height={160}
              className="w-full h-full"
            />
            
            {/* Glass reflection effect */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 30%, transparent 60%)',
              pointerEvents: 'none'
            }}></div>
          </div>
          
          {/* Glass edge highlight */}
          <div className="absolute inset-0 rounded-full" style={{
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.2) 0%, transparent 50%)',
            pointerEvents: 'none'
          }}></div>
        </div>
      )}
    </div>
  );
});

LightingCanvas.displayName = 'LightingCanvas';

export default LightingCanvas;       
